import { useContext, useEffect, useMemo, useState } from "react";
import { Badge, Button, Card, Col, Form, Modal, ProgressBar, Row, Spinner, Table } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import ReactApexChart from "react-apexcharts";
import api from "../../../api";
import { ThemeContext } from "../../../context/ThemeContext";
import PageTitle from "../../layouts/PageTitle";

const formatCurrency = (value) =>
  `₹${Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatCompactCurrency = (value) => {
  const num = Number(value || 0);
  const abs = Math.abs(num);
  const sign = num < 0 ? "-" : "";
  if (abs >= 10000000) return `${sign}₹${(abs / 10000000).toFixed(2)} Cr`;
  if (abs >= 100000) return `${sign}₹${(abs / 100000).toFixed(2)} L`;
  if (abs >= 1000) return `${sign}₹${(abs / 1000).toFixed(1)} K`;
  return `${sign}₹${abs.toLocaleString("en-IN")}`;
};

const formatPercent = (value) => `${Number(value || 0).toFixed(2)}%`;

const COLOR_MAP = {
  "text-primary": "#0078D7",
  "text-success": "#10B981",
  "text-warning": "#F59E0B",
  "text-danger": "#EF4444",
  "text-info": "#06B6D4",
  "text-secondary": "#64748B",
};
const getColorHex = (colorClass) => COLOR_MAP[colorClass] || "#0078D7";

const StatCard = ({ label, value, icon, colorClass, sub, isDark }) => {
  const colorHex = getColorHex(colorClass);
  return (
    <div
      className="card border-0 shadow-sm h-100 transition-all"
      style={{
        borderLeft: `4px solid ${colorHex}`,
        background: isDark ? "#202020" : "#ffffff",
        borderRadius: "10px",
      }}
    >
      <div className="card-body d-flex align-items-center p-3 gap-3">
        <div
          className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
          style={{
            width: 44,
            height: 44,
            background: isDark ? "rgba(255, 255, 255, 0.05)" : `${colorHex}15`,
            border: isDark ? `1px solid rgba(255, 255, 255, 0.08)` : `1px solid ${colorHex}25`,
          }}
        >
          {icon && <i className={icon} style={{ color: colorHex, fontSize: 16 }} />}
        </div>
        <div className="overflow-hidden flex-grow-1">
          <small
            className="text-muted text-uppercase d-block text-truncate fw-semibold"
            style={{ fontSize: "10px", letterSpacing: "0.5px" }}
            title={label}
          >
            {label}
          </small>
          <h5
            className="fw-bold mb-0 text-truncate mt-1"
            style={{
              color: isDark ? "#ffffff" : "#0f172a",
              fontSize: "15px",
            }}
            title={typeof value === "string" ? value : undefined}
          >
            {value}
          </h5>
          {sub && (
            <small className="text-muted d-block text-truncate mt-1" style={{ fontSize: "10px" }}>
              {sub}
            </small>
          )}
        </div>
      </div>
    </div>
  );
};

const getStatusVariant = (status = "") => {
  const normalized = status.toLowerCase();
  if (["approved", "completed", "closed", "paid"].includes(normalized)) return "success";
  if (["pending", "draft"].includes(normalized)) return "warning";
  if (["in progress"].includes(normalized)) return "info";
  if (["rejected", "cancelled"].includes(normalized)) return "danger";
  return "secondary";
};

const getTermLabel = (term, paymentMode) => {
  if (paymentMode === "CUSTOM") return term.name || "Custom Milestone";
  return term.stage || term.name || "Payment Stage";
};

const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  (typeof window !== "undefined" && window.location.hostname === "localhost"
    ? "http://localhost:5001"
    : typeof window !== "undefined"
      ? window.location.origin
      : "");

const getFileUrl = (url) => {
  if (!url) return "#";
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  const cleanBase = API_BASE_URL.replace(/\/api\/?$/, "");
  const cleanPath = url.startsWith("/") ? url : `/${url}`;
  return `${cleanBase}${cleanPath}`;
};

const emptySummary = {
  workOrderCount: 0,
  totalValue: 0,
  totalTaxAmount: 0,
  totalAmount: 0,
  totalDeductionAmount: 0,
  totalPayableAmount: 0,
  totalPayableWithTax: 0,
  totalExpenseAmount: 0,
  totalDirectExpenseAmount: 0,
  totalPoAmount: 0,
  totalContractorBillingAmount: 0,
  totalCostAmount: 0,
};

const emptyBillingSummary = {
  count: 0,
  totalAmount: 0,
  paidAmount: 0,
  pendingAmount: 0,
};

const Payments = () => {
  const { background } = useContext(ThemeContext);
  const isDark = background?.value === "dark" || (typeof document !== "undefined" && document.body.getAttribute("data-theme-version") === "dark");

  const location = useLocation();
  const navigate = useNavigate();
  const query = new URLSearchParams(location.search);
  const projectFromUrl = query.get("project") || "";

  const [projects, setProjects] = useState([]);
  const [projectCards, setProjectCards] = useState([]);
  const [selectedProject, setSelectedProject] = useState(projectFromUrl);
  const [paymentData, setPaymentData] = useState(null);
  const [billingOverview, setBillingOverview] = useState(null);
  const [invoiceList, setInvoiceList] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [costFilters, setCostFilters] = useState({});
  const [projectCostFilter, setProjectCostFilter] = useState("ALL");
  const [projectCostWoFilter, setProjectCostWoFilter] = useState("ALL");
  const [projectCostSearch, setProjectCostSearch] = useState("");
  const [headerSearch, setHeaderSearch] = useState("");
  const [headerClient, setHeaderClient] = useState("");
  const [headerStatus, setHeaderStatus] = useState("ALL");
  const [headerSort, setHeaderSort] = useState("WO_VALUE_DESC");
  const [chartMetricView, setChartMetricView] = useState("ALL");
  const [chartProjectLimit, setChartProjectLimit] = useState(6);

  useEffect(() => {
    setSelectedProject(projectFromUrl);
  }, [projectFromUrl]);

  useEffect(() => {
    const controller = new AbortController();

    const loadProjects = async () => {
      try {
        setProjectsLoading(true);
        const res = await api.get("/projects?limit=1000", { signal: controller.signal });
        setProjects(res.data?.data || []);
      } catch (error) {
        if (error.name !== "CanceledError") {
          console.error("Project load failed:", error);
          setProjects([]);
        }
      } finally {
        setProjectsLoading(false);
      }
    };

    loadProjects();
    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (selectedProject) {
      setProjectCards([]);
      return;
    }

    const controller = new AbortController();

    const loadOverview = async () => {
      try {
        setOverviewLoading(true);
        const [paymentsRes, billingRes, billingListRes] = await Promise.all([
          api.get("/payments", { signal: controller.signal }),
          api.get("/billing/overview", { signal: controller.signal }),
          api.get("/billing?limit=1000", { signal: controller.signal }),
        ]);
        const cards = (paymentsRes.data?.data || []).map((item) => ({
          project: item.project,
          summary: item.summary || emptySummary,
          workOrders: [],
          failed: false,
        }));
        setProjectCards(cards);
        setBillingOverview(billingRes.data?.data || null);
        setInvoiceList(billingListRes.data?.data || []);
      } catch (error) {
        if (error.name !== "CanceledError") {
          console.error("Payments overview load failed:", error);
          setProjectCards([]);
          setBillingOverview(null);
          setInvoiceList([]);
        }
      } finally {
        setOverviewLoading(false);
      }
    };

    loadOverview();
    return () => controller.abort();
  }, [selectedProject]);

  useEffect(() => {
    if (!selectedProject) {
      setPaymentData(null);
      setInvoiceList([]);
      return;
    }

    const controller = new AbortController();

    const loadPayments = async () => {
      try {
        setPaymentsLoading(true);
        const [paymentsRes, billingOverviewRes, billingListRes] = await Promise.all([
          api.get(`/payments/project/${selectedProject}`, { signal: controller.signal }),
          api.get(`/billing/overview?project=${selectedProject}`, { signal: controller.signal }),
          api.get(`/billing?project=${selectedProject}&limit=1000`, { signal: controller.signal }),
        ]);
        setPaymentData(paymentsRes.data || null);
        setBillingOverview(billingOverviewRes.data?.data || null);
        setInvoiceList(billingListRes.data?.data || []);
      } catch (error) {
        if (error.name !== "CanceledError") {
          console.error("Payments load failed:", error);
          setPaymentData(null);
          setBillingOverview(null);
          setInvoiceList([]);
        }
      } finally {
        setPaymentsLoading(false);
      }
    };

    loadPayments();
    return () => controller.abort();
  }, [selectedProject]);

  const workOrders = paymentData?.data || [];
  const summary = paymentData?.summary || emptySummary;
  const billingSummary = billingOverview?.summary || emptyBillingSummary;

  const selectedProjectName = useMemo(() => {
    if (paymentData?.project?.projectName) return paymentData.project.projectName;
    const project = projects.find((item) => item._id === selectedProject);
    return project?.projectName || "Select Project";
  }, [paymentData, projects, selectedProject]);

  const projectCostTrack = useMemo(() => {
    if (!paymentData?.data) return [];
    const items = [];
    const seenKeys = new Set();

    (paymentData.data || []).forEach((wo) => {
      const woLabel = wo.woNo || wo.woNumber || "Work Order";
      (wo.costTrack || []).forEach((item) => {
        const key = `${item.categoryType}_${item._id}`;
        if (!seenKeys.has(key)) {
          seenKeys.add(key);
          items.push({
            ...item,
            workOrderId: String(wo._id),
            workOrderNo: woLabel,
          });
        }
      });
    });

    (paymentData.projectExpenseTrack || []).forEach((item) => {
      const key = `${item.categoryType}_${item._id}`;
      if (!seenKeys.has(key)) {
        seenKeys.add(key);
        items.push({
          ...item,
          workOrderId: "PROJECT",
          workOrderNo: "Site / Project Level",
        });
      }
    });

    return items.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
  }, [paymentData]);

  const filteredProjectCostTrack = useMemo(() => {
    return projectCostTrack.filter((item) => {
      if (projectCostFilter !== "ALL" && item.categoryType !== projectCostFilter) {
        return false;
      }
      if (projectCostWoFilter !== "ALL" && item.workOrderId !== projectCostWoFilter) {
        return false;
      }
      if (projectCostSearch.trim()) {
        const query = projectCostSearch.toLowerCase();
        const vendor = (item.paidTo || item.vendorName || "").toLowerCase();
        const docNo = (item.docNo || item.invoiceNumber || "").toLowerCase();
        const category = (item.category || "").toLowerCase();
        const remarks = (item.remarks || "").toLowerCase();
        const wo = (item.workOrderNo || "").toLowerCase();
        return (
          vendor.includes(query) ||
          docNo.includes(query) ||
          category.includes(query) ||
          remarks.includes(query) ||
          wo.includes(query)
        );
      }
      return true;
    });
  }, [projectCostTrack, projectCostFilter, projectCostWoFilter, projectCostSearch]);


  const invoiceByWorkOrder = useMemo(() => {
    return invoiceList.reduce((acc, invoice) => {
      const workOrder = invoice.workOrder;
      const workOrderId = typeof workOrder === "object" ? workOrder?._id : workOrder;
      if (!workOrderId) return acc;
      const row = acc[workOrderId] || {
        count: 0,
        totalAmount: 0,
        paidAmount: 0,
        pendingAmount: 0,
      };
      const totalAmount = Number(invoice.totalAmount || 0);
      const paidAmount = Number(invoice.paidAmount || 0);
      const deductionAmount = Number(invoice.totalDeductionAmount || 0);
      row.count += 1;
      row.totalAmount += totalAmount;
      row.paidAmount += paidAmount;
      row.totalDeductionAmount = (row.totalDeductionAmount || 0) + deductionAmount;
      row.pendingAmount += Math.max(totalAmount - (paidAmount + deductionAmount), 0);
      acc[workOrderId] = row;
      return acc;
    }, {});
  }, [invoiceList]);

  const invoiceByProject = useMemo(() => {
    return invoiceList.reduce((acc, invoice) => {
      const project = invoice.project;
      const projectId = typeof project === "object" ? project?._id : project;
      if (!projectId) return acc;
      const row = acc[projectId] || {
        count: 0,
        totalAmount: 0,
        paidAmount: 0,
        pendingAmount: 0,
      };
      const totalAmount = Number(invoice.totalAmount || 0);
      const paidAmount = Number(invoice.paidAmount || 0);
      const deductionAmount = Number(invoice.totalDeductionAmount || 0);
      row.count += 1;
      row.totalAmount += totalAmount;
      row.paidAmount += paidAmount;
      row.totalDeductionAmount = (row.totalDeductionAmount || 0) + deductionAmount;
      row.pendingAmount += Math.max(totalAmount - (paidAmount + deductionAmount), 0);
      acc[projectId] = row;
      return acc;
    }, {});
  }, [invoiceList]);

  const uniqueClients = useMemo(() => {
    const set = new Set();
    projects.forEach((p) => {
      const c = p.client?.companyName || p.client?.clientName || p.client?.name;
      if (c && c.trim()) set.add(c.trim());
    });
    return Array.from(set).sort();
  }, [projects]);

  const filteredProjectCards = useMemo(() => {
    let result = [...projectCards];

    if (headerSearch.trim()) {
      const q = headerSearch.toLowerCase().trim();
      result = result.filter((c) => {
        const name = (c.project?.projectName || "").toLowerCase();
        const code = (c.project?.projectCode || "").toLowerCase();
        const client = (c.project?.client?.companyName || c.project?.client?.clientName || c.project?.client?.name || "").toLowerCase();
        return name.includes(q) || code.includes(q) || client.includes(q);
      });
    }

    if (headerClient) {
      result = result.filter((c) => {
        const client = (c.project?.client?.companyName || c.project?.client?.clientName || c.project?.client?.name || "");
        return client.trim() === headerClient.trim();
      });
    }

    if (headerStatus === "PENDING") {
      result = result.filter((c) => {
        const inv = invoiceByProject[c.project?._id];
        return Number(inv?.pendingAmount || 0) > 0;
      });
    } else if (headerStatus === "COLLECTED") {
      result = result.filter((c) => {
        const inv = invoiceByProject[c.project?._id];
        return Number(inv?.paidAmount || 0) > 0 && Number(inv?.pendingAmount || 0) <= 0;
      });
    } else if (headerStatus === "HIGH_VALUE") {
      result = result.filter((c) => Number(c.summary?.totalValue || 0) >= 5000000);
    } else if (headerStatus === "HAS_OUTFLOW") {
      result = result.filter((c) => Number(c.summary?.totalCostAmount || c.summary?.totalExpenseAmount || 0) > 0);
    }

    result.sort((a, b) => {
      if (headerSort === "WO_VALUE_DESC") {
        return Number(b.summary?.totalValue || 0) - Number(a.summary?.totalValue || 0);
      }
      if (headerSort === "BILLED_DESC") {
        const billedA = Number(invoiceByProject[a.project?._id]?.totalAmount || 0);
        const billedB = Number(invoiceByProject[b.project?._id]?.totalAmount || 0);
        return billedB - billedA;
      }
      if (headerSort === "PENDING_DESC") {
        const pendingA = Number(invoiceByProject[a.project?._id]?.pendingAmount || 0);
        const pendingB = Number(invoiceByProject[b.project?._id]?.pendingAmount || 0);
        return pendingB - pendingA;
      }
      if (headerSort === "OUTFLOW_DESC") {
        const outflowA = Number(a.summary?.totalCostAmount || a.summary?.totalExpenseAmount || 0);
        const outflowB = Number(b.summary?.totalCostAmount || b.summary?.totalExpenseAmount || 0);
        return outflowB - outflowA;
      }
      if (headerSort === "NAME_ASC") {
        return (a.project?.projectName || "").localeCompare(b.project?.projectName || "");
      }
      return 0;
    });

    return result;
  }, [projectCards, headerSearch, headerClient, headerStatus, headerSort, invoiceByProject]);

  const hasActiveFilters = Boolean(
    headerSearch.trim() || headerClient || headerStatus !== "ALL" || headerSort !== "WO_VALUE_DESC"
  );

  const resetHeaderFilters = () => {
    setHeaderSearch("");
    setHeaderClient("");
    setHeaderStatus("ALL");
    setHeaderSort("WO_VALUE_DESC");
  };

  const filteredBillingSummary = useMemo(() => {
    if (!hasActiveFilters) {
      return billingOverview?.summary || emptyBillingSummary;
    }
    const filteredProjectIds = new Set(filteredProjectCards.map((c) => String(c.project?._id)));
    let count = 0;
    let totalAmount = 0;
    let paidAmount = 0;
    let pendingAmount = 0;
    let totalDeductions = 0;

    invoiceList.forEach((inv) => {
      const projId = String(inv.project?._id || inv.project || "");
      if (filteredProjectIds.has(projId)) {
        count += 1;
        totalAmount += Number(inv.totalAmount || 0);
        paidAmount += Number(inv.paidAmount || 0);
        const invDeductions =
          (inv.deductions || []).reduce((sum, d) => sum + Number(d.amount || 0), 0) ||
          Number(inv.totalDeductionAmount || 0);
        totalDeductions += invDeductions;
      }
    });

    pendingAmount = Math.max(0, totalAmount - (paidAmount + totalDeductions));
    return { count, totalAmount, paidAmount, pendingAmount, totalDeductions };
  }, [billingOverview, hasActiveFilters, filteredProjectCards, invoiceList]);

  const activeBillingSummary = !selectedProject && hasActiveFilters ? filteredBillingSummary : billingSummary;
  const collectionPercent =
    activeBillingSummary.totalAmount > 0
      ? (Number(activeBillingSummary.paidAmount || 0) / Number(activeBillingSummary.totalAmount || 0)) * 100
      : 0;

  const overviewSummary = useMemo(
    () =>
      filteredProjectCards.reduce(
        (total, card) => ({
          projectCount: total.projectCount + 1,
          workOrderCount: total.workOrderCount + Number(card.summary.workOrderCount || 0),
          totalValue: total.totalValue + Number(card.summary.totalValue || 0),
          totalTaxAmount: total.totalTaxAmount + Number(card.summary.totalTaxAmount || 0),
          totalAmount: total.totalAmount + Number(card.summary.totalAmount || 0),
          totalDeductionAmount:
            total.totalDeductionAmount + Number(card.summary.totalDeductionAmount || 0),
          totalPayableAmount:
            total.totalPayableAmount + Number(card.summary.totalPayableAmount || 0),
          totalPayableWithTax:
            total.totalPayableWithTax + Number(card.summary.totalPayableWithTax || 0),
          totalExpenseAmount:
            total.totalExpenseAmount + Number(card.summary.totalExpenseAmount || card.summary.totalCostAmount || 0),
          totalCostAmount:
            (total.totalCostAmount || 0) + Number(card.summary.totalCostAmount || card.summary.totalExpenseAmount || 0),
          totalPoAmount:
            (total.totalPoAmount || 0) + Number(card.summary.totalPoAmount || 0),
          totalContractorBillingAmount:
            (total.totalContractorBillingAmount || 0) + Number(card.summary.totalContractorBillingAmount || 0),
        }),
        { ...emptySummary, projectCount: 0 }
      ),
    [filteredProjectCards]
  );

  const handleProjectChange = (projectId) => {
    setSelectedProject(projectId);
    if (projectId) {
      navigate(`/payments?project=${projectId}`);
    } else {
      navigate("/payments");
    }
  };

  const handleViewInvoice = async (invoiceId) => {
    if (!invoiceId) return;
    try {
      setInvoiceLoading(true);
      const res = await api.get(`/billing/${invoiceId}`);
      setSelectedInvoice(res.data?.data || null);
    } catch (error) {
      console.error("Invoice detail load failed:", error);
      setSelectedInvoice(null);
    } finally {
      setInvoiceLoading(false);
    }
  };

  const renderSummaryCards = (items, cols = null) => {
    const count = items.length;
    const xlCol = cols?.xl || (count === 8 || count === 4 ? 3 : count <= 6 ? 2 : 3);
    const mdCol = cols?.md || (xlCol === 3 ? 6 : 4);
    const xsCol = cols?.xs || 6;

    return (
      <Row className="g-3 mb-4">
        {items.map((item) => (
          <Col xs={xsCol} md={mdCol} xl={xlCol} key={item.label}>
            <StatCard
              label={item.label}
              value={item.value}
              icon={item.icon}
              colorClass={item.color}
              sub={item.sub}
              isDark={isDark}
            />
          </Col>
        ))}
      </Row>
    );
  };

  const totalInvoiceDeductions = useMemo(() => {
    return invoiceList.reduce(
      (sum, inv) => sum + Number(inv.totalDeductionAmount || 0),
      0
    );
  }, [invoiceList]);

  const renderInvoiceSummaryCards = () =>
    renderSummaryCards([
      { label: "Invoices Generated", value: billingSummary.count || invoiceList.length || 0, icon: "fas fa-file-invoice", color: "text-primary" },
      { label: "Bill Generated", value: formatCurrency(billingSummary.totalAmount), icon: "fas fa-receipt", color: "text-secondary" },
      { label: "Amount Paid", value: formatCurrency(billingSummary.paidAmount), icon: "fas fa-check-circle", color: "text-success" },
      {
        label: "Invoice Deductions",
        value: formatCurrency(billingSummary.totalDeductions || totalInvoiceDeductions),
        icon: "fas fa-percent",
        color: "text-warning",
      },
      { label: "Payment Pending", value: formatCurrency(billingSummary.pendingAmount), icon: "fas fa-clock", color: "text-danger" },
      { label: "Collection %", value: formatPercent(collectionPercent), icon: "fas fa-chart-pie", color: "text-info" },
    ]);

  const renderOverview = () => {
    if (projectsLoading || overviewLoading) {
      return (
        <div className="text-center py-5">
          <Spinner animation="border" />
        </div>
      );
    }

    return (
      <>
        {renderSummaryCards([
          { label: "Projects", value: overviewSummary.projectCount || projectCards.length, icon: "fas fa-folder", color: "text-primary" },
          { label: "Work Orders", value: overviewSummary.workOrderCount, icon: "fas fa-file-contract", color: "text-info" },
          { label: "WO Value", value: formatCurrency(overviewSummary.totalValue), icon: "fas fa-coins", color: "text-primary" },
          { label: "With GST", value: formatCurrency(overviewSummary.totalAmount), icon: "fas fa-receipt", color: "text-secondary" },
          { label: "Deductions", value: formatCurrency(overviewSummary.totalDeductionAmount), icon: "fas fa-percent", color: "text-warning" },
          { label: "Receivable + GST", value: formatCurrency(overviewSummary.totalPayableWithTax), icon: "fas fa-hand-holding-usd", color: "text-success" },
          { label: "Expenses", value: formatCurrency(overviewSummary.totalCostAmount || overviewSummary.totalExpenseAmount), icon: "fas fa-money-bill-wave", color: "text-danger" },
          {
            label: "Est. Gross Margin",
            value: formatCurrency(Math.max(0, (overviewSummary.totalValue || 0) - (overviewSummary.totalCostAmount || overviewSummary.totalExpenseAmount || 0))),
            icon: "fas fa-chart-line",
            color: "text-success",
          },
        ])}

        <div className="d-flex align-items-center justify-content-between mb-3">
          <h6 className="fw-bold mb-0 d-flex align-items-center" style={{ color: isDark ? "#ffffff" : "#0f172a" }}>
            <i className="fas fa-file-invoice text-primary me-2" />
            Invoice &amp; Collection Summary
          </h6>
          <Badge bg="primary-subtle" className="text-primary border border-primary-subtle">
            {hasActiveFilters ? `Filtered (${filteredProjectCards.length} Projects)` : "All Projects Consolidated"}
          </Badge>
        </div>
        {renderInvoiceSummaryCards()}

        {/* Visual Charts in Overview */}
        {filteredProjectCards.length > 0 && (() => {
          const chartProjects = filteredProjectCards.slice(0, chartProjectLimit);
          const totalBilled = Number(activeBillingSummary.totalAmount || 0);
          const totalPaid = Number(activeBillingSummary.paidAmount || 0);
          const totalPending = Number(activeBillingSummary.pendingAmount || 0);
          const totalDeductions = Number(activeBillingSummary.totalDeductions || 0);

          const paidPct = totalBilled > 0 ? ((totalPaid / totalBilled) * 100).toFixed(1) : "0.0";
          const pendingPct = totalBilled > 0 ? ((totalPending / totalBilled) * 100).toFixed(1) : "0.0";
          const deductPct = totalBilled > 0 ? ((totalDeductions / totalBilled) * 100).toFixed(1) : "0.0";

          let series = [];
          let colors = [];
          if (chartMetricView === "ALL") {
            series = [
              { name: "WO Value", data: chartProjects.map((c) => Number(c.summary?.totalValue || 0)) },
              { name: "Billed Invoices", data: chartProjects.map((c) => Number(invoiceByProject[c.project?._id]?.totalAmount || 0)) },
              { name: "Paid / Received", data: chartProjects.map((c) => Number(invoiceByProject[c.project?._id]?.paidAmount || 0)) },
              { name: "Total Outflow", data: chartProjects.map((c) => Number(c.summary?.totalCostAmount || c.summary?.totalExpenseAmount || 0)) },
            ];
            colors = ["#4F46E5", "#0284C7", "#10B981", "#EF4444"];
          } else if (chartMetricView === "REVENUE_OUTFLOW") {
            series = [
              { name: "WO Value (Revenue)", data: chartProjects.map((c) => Number(c.summary?.totalValue || 0)) },
              { name: "Total Outflow (Expense)", data: chartProjects.map((c) => Number(c.summary?.totalCostAmount || c.summary?.totalExpenseAmount || 0)) },
            ];
            colors = ["#4F46E5", "#EF4444"];
          } else {
            series = [
              { name: "Billed Invoices", data: chartProjects.map((c) => Number(invoiceByProject[c.project?._id]?.totalAmount || 0)) },
              { name: "Paid / Received", data: chartProjects.map((c) => Number(invoiceByProject[c.project?._id]?.paidAmount || 0)) },
            ];
            colors = ["#0284C7", "#10B981"];
          }

          return (
            <Row className="g-3 mb-4">
              {/* Left: Projects Financial Overview */}
              <Col xl={7} lg={7}>
                <Card
                  className="shadow-sm h-100 border-0"
                  style={{
                    background: isDark ? "#202020" : "#ffffff",
                    borderRadius: 14,
                    border: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid #e2e8f0",
                  }}
                >
                  <Card.Header className="border-0 bg-transparent py-3 px-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
                    <div>
                      <h6 className="fw-bold mb-0" style={{ color: isDark ? "#ffffff" : "#0f172a" }}>
                        <i className="fas fa-chart-bar text-primary me-2" />
                        Projects Financial Overview
                      </h6>
                      <small className="text-muted">Portfolio cashflow, billings and cost comparison</small>
                    </div>

                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      {/* Metric Mode Toggle */}
                      <div
                        className="d-flex align-items-center gap-2"
                        role="group"
                        style={{ height: "36px" }}
                      >
                        <Button
                          size="sm"
                          variant={
                            chartMetricView === "ALL"
                              ? "primary"
                              : isDark
                                ? "outline-secondary"
                                : "outline-primary"
                          }
                          className="d-inline-flex align-items-center justify-content-center"
                          style={{
                            height: "36px",
                            minHeight: "36px",
                            maxHeight: "36px",
                            padding: "0 14px",
                            lineHeight: "1",
                            fontSize: "12px",
                            fontWeight: "500",
                            borderRadius: "18px",
                            boxShadow: "none",
                            boxSizing: "border-box",
                            color: chartMetricView === "ALL" ? "#ffffff" : isDark ? "#e2e8f0" : undefined,
                            borderColor: chartMetricView === "ALL" ? undefined : isDark ? "rgba(255,255,255,0.2)" : undefined,
                            backgroundColor: chartMetricView === "ALL" ? undefined : isDark ? "rgba(255,255,255,0.05)" : undefined,
                          }}
                          onClick={() => setChartMetricView("ALL")}
                        >
                          All ({filteredProjectCards.length})
                        </Button>

                        <Button
                          size="sm"
                          variant={
                            chartMetricView === "REVENUE_OUTFLOW"
                              ? "primary"
                              : isDark
                                ? "outline-secondary"
                                : "outline-primary"
                          }
                          className="d-inline-flex align-items-center justify-content-center"
                          style={{
                            height: "36px",
                            minHeight: "36px",
                            maxHeight: "36px",
                            padding: "0 14px",
                            lineHeight: "1",
                            fontSize: "12px",
                            fontWeight: "500",
                            borderRadius: "18px",
                            boxShadow: "none",
                            boxSizing: "border-box",
                            color: chartMetricView === "REVENUE_OUTFLOW" ? "#ffffff" : isDark ? "#e2e8f0" : undefined,
                            borderColor: chartMetricView === "REVENUE_OUTFLOW" ? undefined : isDark ? "rgba(255,255,255,0.2)" : undefined,
                            backgroundColor: chartMetricView === "REVENUE_OUTFLOW" ? undefined : isDark ? "rgba(255,255,255,0.05)" : undefined,
                          }}
                          onClick={() => setChartMetricView("REVENUE_OUTFLOW")}
                        >
                          WO vs Outflows
                        </Button>

                        <Button
                          size="sm"
                          variant={
                            chartMetricView === "BILLED_PAID"
                              ? "primary"
                              : isDark
                                ? "outline-secondary"
                                : "outline-primary"
                          }
                          className="d-inline-flex align-items-center justify-content-center"
                          style={{
                            height: "36px",
                            minHeight: "36px",
                            maxHeight: "36px",
                            padding: "0 14px",
                            lineHeight: "1",
                            fontSize: "12px",
                            fontWeight: "500",
                            borderRadius: "18px",
                            boxShadow: "none",
                            boxSizing: "border-box",
                            color: chartMetricView === "BILLED_PAID" ? "#ffffff" : isDark ? "#e2e8f0" : undefined,
                            borderColor: chartMetricView === "BILLED_PAID" ? undefined : isDark ? "rgba(255,255,255,0.2)" : undefined,
                            backgroundColor: chartMetricView === "BILLED_PAID" ? undefined : isDark ? "rgba(255,255,255,0.05)" : undefined,
                          }}
                          onClick={() => setChartMetricView("BILLED_PAID")}
                        >
                          Billed vs Paid
                        </Button>
                      </div>

                      {/* Project Count Limit */}
                      <Form.Select
                        size="sm"
                        value={chartProjectLimit}
                        onChange={(e) => setChartProjectLimit(Number(e.target.value))}
                        style={{
                          height: "36px",
                          minHeight: "36px",
                          maxHeight: "36px",
                          width: "auto",
                          minWidth: "145px",
                          borderRadius: "18px",
                          fontSize: "12px",
                          fontWeight: "500",
                          paddingTop: 0,
                          paddingBottom: 0,
                          paddingLeft: "14px",
                          paddingRight: "34px",
                          backgroundColor: isDark ? "#0f172a" : "#ffffff",
                          borderColor: isDark
                            ? "rgba(255,255,255,0.12)"
                            : "#ced4da",
                          color: isDark ? "#ffffff" : "#0f172a",
                          boxShadow: "none",
                          boxSizing: "border-box",
                          cursor: "pointer",
                        }}
                      >
                        <option value={6}>Top 6 Projects</option>
                        <option value={10}>Top 10 Projects</option>
                        <option value={100}>
                          All Filtered ({filteredProjectCards.length})
                        </option>
                      </Form.Select>
                    </div>

                  </Card.Header>

                  {/* Quick Metrics Bar inside Card */}
                  <div
                    className="mx-3 p-2 rounded d-flex justify-content-between align-items-center flex-wrap gap-2"
                    style={{
                      background: isDark ? "rgba(255,255,255,0.03)" : "#f8fafc",
                      border: isDark ? "1px solid rgba(255,255,255,0.06)" : "1px solid #e2e8f0",
                    }}
                  >
                    <div className="d-flex align-items-center gap-1">
                      <span className="text-muted small">💼 WO Portfolio:</span>
                      <strong className="small" style={{ color: isDark ? "#818cf8" : "#4338ca" }}>
                        {formatCompactCurrency(overviewSummary.totalValue)}
                      </strong>
                    </div>
                    <div className="d-flex align-items-center gap-1">
                      <span className="text-muted small">📑 Billed:</span>
                      <strong className="small text-info">
                        {formatCompactCurrency(activeBillingSummary.totalAmount)}
                      </strong>
                    </div>
                    <div className="d-flex align-items-center gap-1">
                      <span className="text-muted small">💰 Paid:</span>
                      <strong className="small text-success">
                        {formatCompactCurrency(activeBillingSummary.paidAmount)}
                      </strong>
                    </div>
                    <div className="d-flex align-items-center gap-1">
                      <span className="text-muted small">💸 Outflows:</span>
                      <strong className="small text-danger">
                        {formatCompactCurrency(overviewSummary.totalCostAmount || overviewSummary.totalExpenseAmount)}
                      </strong>
                    </div>
                  </div>

                  <Card.Body className="p-3">
                    {chartProjects.length === 0 ? (
                      <div className="text-center py-5 text-muted">No projects available for chart visualization</div>
                    ) : (
                      <ReactApexChart
                        options={{
                          chart: {
                            type: "bar",
                            height: 310,
                            toolbar: { show: false },
                            background: "transparent",
                            fontFamily: "Poppins, sans-serif",
                          },
                          theme: { mode: isDark ? "dark" : "light" },
                          plotOptions: {
                            bar: {
                              horizontal: false,
                              columnWidth: chartMetricView === "ALL" ? "62%" : "42%",
                              borderRadius: 4,
                              borderRadiusApplication: "end",
                            },
                          },
                          dataLabels: { enabled: false },
                          stroke: { show: true, width: 2, colors: ["transparent"] },
                          xaxis: {
                            categories: chartProjects.map((c) => c.project?.projectName || "Project"),
                            labels: {
                              trim: true,
                              rotate: -25,
                              style: {
                                fontSize: "11px",
                                fontFamily: "Poppins, sans-serif",
                                colors: isDark ? "#94a3b8" : "#64748b",
                              },
                            },
                            axisBorder: { color: isDark ? "rgba(255,255,255,0.1)" : "#e2e8f0" },
                            axisTicks: { color: isDark ? "rgba(255,255,255,0.1)" : "#e2e8f0" },
                          },
                          yaxis: {
                            labels: {
                              formatter: (val) => formatCompactCurrency(val),
                              style: {
                                colors: isDark ? "#94a3b8" : "#64748b",
                                fontSize: "11px",
                                fontFamily: "Poppins, sans-serif",
                              },
                            },
                          },
                          grid: {
                            borderColor: isDark ? "rgba(255, 255, 255, 0.08)" : "#f1f5f9",
                            strokeDashArray: 3,
                          },
                          colors: colors,
                          legend: {
                            position: "top",
                            horizontalAlign: "right",
                            labels: { colors: isDark ? "#cbd5e1" : "#334155" },
                            fontSize: "12px",
                            markers: { radius: 3 },
                          },
                          tooltip: {
                            theme: isDark ? "dark" : "light",
                            y: { formatter: (val) => formatCurrency(val) },
                          },
                        }}
                        series={series}
                        type="bar"
                        height={310}
                      />
                    )}
                  </Card.Body>
                </Card>
              </Col>

              {/* Right: Overall Collection & Settlement */}
              <Col xl={5} lg={5}>
                <Card
                  className="shadow-sm h-100 border-0"
                  style={{
                    background: isDark ? "#202020" : "#ffffff",
                    borderRadius: 14,
                    border: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid #e2e8f0",
                  }}
                >
                  <Card.Header className="border-0 bg-transparent py-3 px-3 d-flex justify-content-between align-items-center">
                    <div>
                      <h6 className="fw-bold mb-0" style={{ color: isDark ? "#ffffff" : "#0f172a" }}>
                        <i className="fas fa-chart-pie text-info me-2" />
                        Overall Collection &amp; Settlement
                      </h6>
                      <small className="text-muted">Invoiced amounts recovery breakdown</small>
                    </div>
                    <Badge
                      bg={Number(paidPct) > 50 ? "success-subtle" : "warning-subtle"}
                      className={
                        Number(paidPct) > 50
                          ? "text-success border border-success-subtle px-2 py-1"
                          : "text-warning-emphasis border border-warning-subtle px-2 py-1"
                      }
                    >
                      Recovery: {paidPct}%
                    </Badge>
                  </Card.Header>

                  <Card.Body className="d-flex flex-column p-3 pt-0">
                    {/* Donut Chart with Centered Value */}
                    <div className="d-flex justify-content-center">
                      <ReactApexChart
                        options={{
                          chart: {
                            type: "donut",
                            height: 220,
                            background: "transparent",
                            fontFamily: "Poppins, sans-serif",
                          },
                          theme: { mode: isDark ? "dark" : "light" },
                          labels: ["Paid / Collected", "Pending Receivable", "Deductions Applied"],
                          colors: ["#10B981", "#F59E0B", "#EF4444"],
                          legend: { show: false },
                          stroke: {
                            colors: [isDark ? "#202020" : "#ffffff"],
                            width: 3,
                          },
                          plotOptions: {
                            pie: {
                              donut: {
                                size: "70%",
                                labels: {
                                  show: true,
                                  name: {
                                    show: true,
                                    color: isDark ? "#94a3b8" : "#64748b",
                                    fontSize: "12px",
                                    fontFamily: "Poppins, sans-serif",
                                  },
                                  value: {
                                    show: true,
                                    color: isDark ? "#ffffff" : "#0f172a",
                                    fontSize: "16px",
                                    fontWeight: 700,
                                    fontFamily: "Poppins, sans-serif",
                                    formatter: (val) => formatCompactCurrency(val),
                                  },
                                  total: {
                                    show: true,
                                    label: "Total Billed",
                                    color: isDark ? "#94a3b8" : "#64748b",
                                    fontFamily: "Poppins, sans-serif",
                                    fontSize: "11px",
                                    formatter: () => formatCompactCurrency(totalBilled),
                                  },
                                },
                              },
                            },
                          },
                          tooltip: {
                            theme: isDark ? "dark" : "light",
                            y: { formatter: (val) => formatCurrency(val) },
                          },
                        }}
                        series={[
                          totalPaid,
                          totalPending,
                          totalDeductions,
                        ]}
                        type="donut"
                        height={220}
                      />
                    </div>

                    {/* Rich Breakdown Metric Cards underneath the Donut */}
                    <div className="d-flex flex-column gap-2 mt-2">
                      {/* Row 1: Paid */}
                      <div
                        className="p-2 px-3 rounded border d-flex align-items-center justify-content-between"
                        style={{
                          background: isDark ? "rgba(16, 185, 129, 0.05)" : "#f0fdf4",
                          borderColor: isDark ? "rgba(16, 185, 129, 0.2)" : "#bbf7d0",
                        }}
                      >
                        <div className="d-flex align-items-center gap-2 flex-grow-1 me-2 min-w-0">
                          <div
                            className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                            style={{ width: 28, height: 28, background: "#10b98125" }}
                          >
                            <i className="fas fa-check text-success" style={{ fontSize: "11px" }} />
                          </div>
                          <div className="flex-grow-1 min-w-0">
                            <div className="d-flex justify-content-between align-items-center">
                              <span className="small fw-semibold" style={{ color: isDark ? "#ffffff" : "#0f172a", fontSize: "12px" }}>
                                Paid / Collected
                              </span>
                              <span className="badge bg-success-subtle text-success" style={{ fontSize: "10px" }}>
                                {paidPct}%
                              </span>
                            </div>
                            <ProgressBar now={Number(paidPct)} variant="success" style={{ height: "4px" }} className="mt-1" />
                          </div>
                        </div>
                        <div className="text-end flex-shrink-0">
                          <div className="fw-bold text-success" style={{ fontSize: "13px" }}>
                            {formatCurrency(totalPaid)}
                          </div>
                        </div>
                      </div>

                      {/* Row 2: Pending */}
                      <div
                        className="p-2 px-3 rounded border d-flex align-items-center justify-content-between"
                        style={{
                          background: isDark ? "rgba(245, 158, 11, 0.05)" : "#fffbeb",
                          borderColor: isDark ? "rgba(245, 158, 11, 0.2)" : "#fde68a",
                        }}
                      >
                        <div className="d-flex align-items-center gap-2 flex-grow-1 me-2 min-w-0">
                          <div
                            className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                            style={{ width: 28, height: 28, background: "#f59e0b25" }}
                          >
                            <i className="fas fa-clock text-warning" style={{ fontSize: "11px" }} />
                          </div>
                          <div className="flex-grow-1 min-w-0">
                            <div className="d-flex justify-content-between align-items-center">
                              <span className="small fw-semibold" style={{ color: isDark ? "#ffffff" : "#0f172a", fontSize: "12px" }}>
                                Pending Receivable
                              </span>
                              <span className="badge bg-warning-subtle text-warning-emphasis" style={{ fontSize: "10px" }}>
                                {pendingPct}%
                              </span>
                            </div>
                            <ProgressBar now={Number(pendingPct)} variant="warning" style={{ height: "4px" }} className="mt-1" />
                          </div>
                        </div>
                        <div className="text-end flex-shrink-0">
                          <div className="fw-bold text-warning-emphasis" style={{ fontSize: "13px" }}>
                            {formatCurrency(totalPending)}
                          </div>
                        </div>
                      </div>

                      {/* Row 3: Deductions */}
                      <div
                        className="p-2 px-3 rounded border d-flex align-items-center justify-content-between"
                        style={{
                          background: isDark ? "rgba(239, 68, 68, 0.05)" : "#fef2f2",
                          borderColor: isDark ? "rgba(239, 68, 68, 0.2)" : "#fecaca",
                        }}
                      >
                        <div className="d-flex align-items-center gap-2 flex-grow-1 me-2 min-w-0">
                          <div
                            className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                            style={{ width: 28, height: 28, background: "#ef444425" }}
                          >
                            <i className="fas fa-scissors text-danger" style={{ fontSize: "11px" }} />
                          </div>
                          <div className="flex-grow-1 min-w-0">
                            <div className="d-flex justify-content-between align-items-center">
                              <span className="small fw-semibold" style={{ color: isDark ? "#ffffff" : "#0f172a", fontSize: "12px" }}>
                                Deductions Applied
                              </span>
                              <span className="badge bg-danger-subtle text-danger" style={{ fontSize: "10px" }}>
                                {deductPct}%
                              </span>
                            </div>
                            <ProgressBar now={Number(deductPct)} variant="danger" style={{ height: "4px" }} className="mt-1" />
                          </div>
                        </div>
                        <div className="text-end flex-shrink-0">
                          <div className="fw-bold text-danger" style={{ fontSize: "13px" }}>
                            {formatCurrency(totalDeductions)}
                          </div>
                        </div>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          );
        })()}

        {filteredProjectCards.length === 0 ? (
          <Card
            className="shadow-sm border-0"
            style={{
              background: isDark ? "#202020" : "#ffffff",
              borderRadius: 12,
              border: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid #e2e8f0",
            }}
          >
            <Card.Body className="text-center py-5 text-muted">
              <i className="fas fa-filter fa-3x mb-3 text-secondary opacity-50" />
              <p className="mb-2 fw-semibold">No projects found matching the selected filters.</p>
              {hasActiveFilters && (
                <Button variant="outline-primary" size="sm" onClick={resetHeaderFilters}>
                  Clear All Filters
                </Button>
              )}
            </Card.Body>
          </Card>
        ) : (
          <Row className="g-4">
            {filteredProjectCards.map((card) => {
              const project = card.project || {};
              const summaryData = card.summary || emptySummary;
              const projectId = project._id;
              const projectInvoices = invoiceByProject[projectId] || emptyBillingSummary;
              const totalBilled = Number(projectInvoices.totalAmount || 0);
              const totalPaid = Number(projectInvoices.paidAmount || 0);
              const collectionPct = totalBilled > 0 ? Math.min(100, Math.round((totalPaid / totalBilled) * 100)) : 0;

              return (
                <Col md={6} xl={4} key={projectId || project.projectName}>
                  <div
                    className="card shadow-sm h-100 border-0 transition-all"
                    style={{
                      background: isDark ? "#202020" : "#ffffff",
                      borderRadius: 12,
                    }}
                  >
                    <div className="card-body d-flex flex-column p-4">
                      <div className="d-flex justify-content-between align-items-start gap-2 mb-3">
                        <div className="flex-grow-1 min-w-0">
                          <h5
                            className="fw-bold mb-1 text-truncate"
                            style={{ color: isDark ? "#ffffff" : "#0f172a" }}
                            title={project.projectName}
                          >
                            {project.projectName || "Project"}
                          </h5>
                          <small className="text-muted d-block text-truncate">
                            <span className="badge bg-secondary-subtle text-secondary me-1" style={{ fontSize: "10px" }}>
                              {project.projectCode || "PRJ"}
                            </span>
                            {project.client?.companyName || project.client?.clientName || ""}
                          </small>
                        </div>
                        <Badge
                          bg={card.failed ? "danger" : "primary-subtle"}
                          className={card.failed ? "" : "text-primary border border-primary-subtle px-2 py-1 rounded-pill"}
                        >
                          {card.failed ? "Error" : `${summaryData.workOrderCount || 0} WO`}
                        </Badge>
                      </div>

                      {/* Collection Progress */}
                      <div className="mb-3 p-2 rounded" style={{ background: isDark ? "rgba(255,255,255,0.03)" : "#f8fafc" }}>
                        <div className="d-flex justify-content-between align-items-center small mb-1">
                          <span className="text-muted" style={{ fontSize: "11px" }}>Collection Ratio</span>
                          <span className="fw-bold" style={{ fontSize: "11px", color: collectionPct >= 80 ? "#10B981" : collectionPct >= 40 ? "#F59E0B" : "#EF4444" }}>
                            {collectionPct}%
                          </span>
                        </div>
                        <ProgressBar
                          now={collectionPct}
                          variant={collectionPct >= 80 ? "success" : collectionPct >= 40 ? "warning" : "danger"}
                          style={{ height: 6, borderRadius: 3 }}
                        />
                      </div>

                      {/* Financial 4-Box Grid */}
                      <div className="row g-2 mb-4">
                        <div className="col-6">
                          <div
                            className="p-2 rounded border text-start h-100"
                            style={{
                              background: isDark ? "rgba(255,255,255,0.02)" : "#f8fafc",
                              borderColor: isDark ? "rgba(255,255,255,0.08)" : "#f0f0f0",
                            }}
                          >
                            <small className="text-muted d-block" style={{ fontSize: "10.5px" }}>WO (With GST)</small>
                            <strong className="d-block text-truncate mt-1" style={{ color: isDark ? "#ffffff" : "#0f172a", fontSize: "13px" }} title={formatCurrency(summaryData.totalAmount)}>
                              {formatCompactCurrency(summaryData.totalAmount)}
                            </strong>
                          </div>
                        </div>
                        <div className="col-6">
                          <div
                            className="p-2 rounded border text-start h-100"
                            style={{
                              background: isDark ? "rgba(255,255,255,0.02)" : "#f8fafc",
                              borderColor: isDark ? "rgba(255,255,255,0.08)" : "#f0f0f0",
                            }}
                          >
                            <small className="text-muted d-block" style={{ fontSize: "10.5px" }}>Bill Generated</small>
                            <strong className="d-block text-truncate mt-1" style={{ color: isDark ? "#ffffff" : "#0f172a", fontSize: "13px" }} title={formatCurrency(projectInvoices.totalAmount)}>
                              {formatCompactCurrency(projectInvoices.totalAmount)}
                            </strong>
                          </div>
                        </div>
                        <div className="col-6">
                          <div
                            className="p-2 rounded border text-start h-100"
                            style={{
                              background: isDark ? "rgba(255,255,255,0.02)" : "#f8fafc",
                              borderColor: isDark ? "rgba(255,255,255,0.08)" : "#f0f0f0",
                            }}
                          >
                            <small className="text-muted d-block" style={{ fontSize: "10.5px" }}>Paid / Received</small>
                            <strong className="d-block text-truncate text-success mt-1" style={{ fontSize: "13px" }} title={formatCurrency(projectInvoices.paidAmount)}>
                              {formatCompactCurrency(projectInvoices.paidAmount)}
                            </strong>
                          </div>
                        </div>
                        <div className="col-6">
                          <div
                            className="p-2 rounded border text-start h-100"
                            style={{
                              background: isDark ? "rgba(255,255,255,0.02)" : "#f8fafc",
                              borderColor: isDark ? "rgba(255,255,255,0.08)" : "#f0f0f0",
                            }}
                          >
                            <small className="text-muted d-block" style={{ fontSize: "10.5px" }}>Expenses / Outflow</small>
                            <strong className="d-block text-truncate text-primary mt-1" style={{ fontSize: "13px" }} title={formatCurrency(summaryData.totalCostAmount || summaryData.totalExpenseAmount)}>
                              {formatCompactCurrency(summaryData.totalCostAmount || summaryData.totalExpenseAmount)}
                            </strong>
                          </div>
                        </div>
                      </div>

                      <Button
                        variant="outline-primary"
                        className="mt-auto rounded-pill py-2 d-flex align-items-center justify-content-center gap-2 fw-semibold"
                        disabled={!projectId || card.failed}
                        onClick={() => handleProjectChange(projectId)}
                      >
                        <span>View Financial Details</span>
                        <i className="fas fa-arrow-right fs-12"></i>
                      </Button>
                    </div>
                  </div>
                </Col>
              );
            })}
          </Row>
        )}
      </>
    );
  };

  return (
    <>
      <div className="mb-3">
        <PageTitle activeMenu="Payments" motherMenu="Commercial" />
      </div>

      {/* Top Header Filter & Search Toolbar */}
      <Card
        className="shadow-sm border-0 mb-4"
        style={{
          background: isDark ? "#202020" : "#ffffff",
          borderRadius: 14,
          border: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid #e2e8f0",
        }}
      >
        <Card.Body className="p-3">
          <Row className="g-2 align-items-center">
            {/* 1. Instant Search Input */}
            <Col xl={selectedProject ? 4 : 3} lg={selectedProject ? 4 : 4} md={selectedProject ? 5 : 6}>
              <div className="input-group input-group-sm" style={{ height: "38px" }}>
                <span
                  className="input-group-text"
                  style={{
                    height: "38px",
                    borderRadius: "20px 0 0 20px",
                    background: isDark ? "#1e293b" : "#f8fafc",
                    borderColor: isDark ? "rgba(255,255,255,0.12)" : "#ced4da",
                    color: isDark ? "#94a3b8" : "#64748b",
                  }}
                >
                  <i className="fas fa-search" />
                </span>
                <Form.Control
                  size="sm"
                  placeholder="Search project, code, client..."
                  value={headerSearch}
                  onChange={(e) => setHeaderSearch(e.target.value)}
                  style={{
                    height: "38px",
                    borderRadius: headerSearch ? "0" : "0 20px 20px 0",
                    background: isDark ? "#0f172a" : "#ffffff",
                    borderColor: isDark ? "rgba(255,255,255,0.12)" : "#ced4da",
                    color: isDark ? "#ffffff" : "#0f172a",
                  }}
                />
                {headerSearch && (
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    style={{
                      height: "38px",
                      borderRadius: "0 20px 20px 0",
                      borderColor: isDark ? "rgba(255,255,255,0.12)" : "#ced4da",
                    }}
                    onClick={() => setHeaderSearch("")}
                  >
                    ✕
                  </Button>
                )}
              </div>
            </Col>

            {/* 2. Project Selector */}
            <Col xl={selectedProject ? 5 : 2} lg={selectedProject ? 5 : 4} md={selectedProject ? 5 : 6}>
              <Form.Select
                size="sm"
                value={selectedProject}
                disabled={projectsLoading}
                onChange={(e) => handleProjectChange(e.target.value)}
                style={{
                  height: "38px",
                  borderRadius: "20px",
                  backgroundColor: isDark ? "#0f172a" : "#ffffff",
                  borderColor: isDark ? "rgba(255,255,255,0.12)" : "#ced4da",
                  color: isDark ? "#ffffff" : "#0f172a",
                }}
              >
                <option value="">📁 All Projects Overview ({projects.length})</option>
                {projects.map((project) => (
                  <option key={project._id} value={project._id}>
                    {project.projectCode ? `${project.projectCode} - ` : ""}
                    {project.projectName}
                  </option>
                ))}
              </Form.Select>
            </Col>

            {/* Back Button inside the same row on the right */}
            {selectedProject && (
              <Col xs="auto" className="ms-auto">
                <Button
                  variant={isDark ? "outline-light" : "outline-primary"}
                  size="sm"
                  className="d-flex align-items-center gap-2 px-3 fw-semibold"
                  style={{
                    height: "38px",
                    minHeight: "38px",
                    maxHeight: "38px",
                    borderRadius: "20px",
                    fontSize: "13px",
                    fontWeight: "600",
                    whiteSpace: "nowrap",
                    color: isDark ? "#ffffff" : "#0078D7",
                    borderColor: isDark ? "rgba(255, 255, 255, 0.35)" : "#0078D7",
                    backgroundColor: isDark ? "rgba(255, 255, 255, 0.1)" : "transparent",
                    transition: "all 0.2s ease",
                  }}
                  onClick={() => handleProjectChange("")}
                >
                  <i className="fas fa-arrow-left" style={{ color: isDark ? "#ffffff" : "#0078D7" }} />
                  <span style={{ color: isDark ? "#ffffff" : "#0078D7" }}>Back to Overview</span>
                </Button>
              </Col>
            )}

            {/* 3. Client Filter (in Overview) */}
            {!selectedProject && (
              <Col xl={2} lg={4} md={6}>
                <Form.Select
                  size="sm"
                  value={headerClient}
                  onChange={(e) => setHeaderClient(e.target.value)}
                  style={{
                    height: "38px",
                    borderRadius: "20px",
                    backgroundColor: isDark ? "#0f172a" : "#ffffff",
                    borderColor: isDark ? "rgba(255,255,255,0.12)" : "#ced4da",
                    color: isDark ? "#ffffff" : "#0f172a",
                  }}
                >
                  <option value="">🏢 All Clients ({uniqueClients.length})</option>
                  {uniqueClients.map((client) => (
                    <option key={client} value={client}>
                      {client}
                    </option>
                  ))}
                </Form.Select>
              </Col>
            )}

            {/* 4. Financial Health / Status Filter (in Overview) */}
            {!selectedProject && (
              <Col xl={2} lg={4} md={6}>
                <Form.Select
                  size="sm"
                  value={headerStatus}
                  onChange={(e) => setHeaderStatus(e.target.value)}
                  style={{
                    height: "38px",
                    borderRadius: "20px",
                    backgroundColor: isDark ? "#0f172a" : "#ffffff",
                    borderColor: isDark ? "rgba(255,255,255,0.12)" : "#ced4da",
                    color: isDark ? "#ffffff" : "#0f172a",
                  }}
                >
                  <option value="ALL">⚡ All Financial Status</option>
                  <option value="PENDING">⚠️ Has Pending Receivables</option>
                  <option value="COLLECTED">✅ Fully Collected</option>
                  <option value="HIGH_VALUE">💎 High Value (&ge; ₹50L)</option>
                  <option value="HAS_OUTFLOW">💸 Active Outflow Recorded</option>
                </Form.Select>
              </Col>
            )}

            {/* 5. Sort By Dropdown (in Overview) */}
            {!selectedProject && (
              <Col xl={2} lg={4} md={6}>
                <Form.Select
                  size="sm"
                  value={headerSort}
                  onChange={(e) => setHeaderSort(e.target.value)}
                  style={{
                    height: "38px",
                    borderRadius: "20px",
                    backgroundColor: isDark ? "#0f172a" : "#ffffff",
                    borderColor: isDark ? "rgba(255,255,255,0.12)" : "#ced4da",
                    color: isDark ? "#ffffff" : "#0f172a",
                  }}
                >
                  <option value="WO_VALUE_DESC">Sort: Highest WO Value</option>
                  <option value="BILLED_DESC">Sort: Highest Billed</option>
                  <option value="PENDING_DESC">Sort: Highest Pending</option>
                  <option value="OUTFLOW_DESC">Sort: Highest Outflow</option>
                  <option value="NAME_ASC">Sort: Project Name (A-Z)</option>
                </Form.Select>
              </Col>
            )}

            {/* 6. Reset Filters Button */}
            {!selectedProject && (
              <Col xl={1} lg={2} md={3} sm={4}>
                <Button
                  variant={hasActiveFilters ? "danger" : "outline-secondary"}
                  size="sm"
                  disabled={!hasActiveFilters}
                  onClick={resetHeaderFilters}
                  className="w-100 d-flex align-items-center justify-content-center gap-1"
                  style={{
                    height: "38px",
                    borderRadius: "20px",
                    fontSize: "12px",
                    fontWeight: "600",
                    background: hasActiveFilters ? "#ef4444" : "transparent",
                    borderColor: hasActiveFilters ? "#ef4444" : (isDark ? "rgba(255,255,255,0.15)" : "#ced4da"),
                    color: hasActiveFilters ? "#ffffff" : (isDark ? "#94a3b8" : "#64748b"),
                    opacity: hasActiveFilters ? 1 : 0.65,
                    cursor: hasActiveFilters ? "pointer" : "default",
                    transition: "all 0.2s ease",
                  }}
                >
                  <i className="fas fa-undo" style={{ fontSize: "11px" }} />
                  <span>Reset</span>
                </Button>
              </Col>
            )}
          </Row>
        </Card.Body>
      </Card>

      {!selectedProject ? (
        renderOverview()
      ) : paymentsLoading ? (
        <div className="text-center py-5">
          <Spinner animation="border" />
        </div>
      ) : (
        <>
          <Card className="mb-4 shadow-sm border-0" style={{ background: isDark ? "#202020" : "#ffffff", borderRadius: 12 }}>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
                <div>
                  <h5 className="fw-bold mb-1" style={{ color: isDark ? "#ffffff" : "#0f172a" }}>{selectedProjectName}</h5>
                  <small className="text-muted">
                    {paymentData?.project?.projectCode || "Project Payments"}
                    {paymentData?.project?.client?.companyName ||
                      paymentData?.project?.client?.clientName
                      ? ` - ${paymentData.project.client.companyName ||
                      paymentData.project.client.clientName
                      }`
                      : ""}
                  </small>
                </div>
                <Badge bg="primary-subtle" className="text-primary border border-primary-subtle px-3 py-2 rounded-pill fw-semibold">
                  {summary.workOrderCount || 0} Work Orders
                </Badge>
              </div>
            </Card.Body>
          </Card>

          {renderSummaryCards([
            { label: "WO Value", value: formatCurrency(summary.totalValue), icon: "fas fa-coins", color: "text-primary" },
            { label: "GST", value: formatCurrency(summary.totalTaxAmount), icon: "fas fa-receipt", color: "text-secondary" },
            { label: "With GST", value: formatCurrency(summary.totalAmount), icon: "fas fa-file-invoice-dollar", color: "text-primary" },
            { label: "Deductions", value: formatCurrency(summary.totalDeductionAmount), icon: "fas fa-percent", color: "text-warning" },
            { label: "Receivable", value: formatCurrency(summary.totalPayableAmount), icon: "fas fa-hand-holding-usd", color: "text-info" },
            { label: "Receivable + GST", value: formatCurrency(summary.totalPayableWithTax), icon: "fas fa-hand-holding-usd", color: "text-success" },
            { label: "Total Outflow", value: formatCurrency(summary.totalCostAmount || summary.totalExpenseAmount), icon: "fas fa-money-bill-wave", color: "text-danger" },
            {
              label: "Est. Gross Margin",
              value: formatCurrency(Math.max(0, (summary.totalValue || 0) - (summary.totalCostAmount || 0))),
              icon: "fas fa-chart-line",
              color: "text-success",
            },
          ])}

          {/* Project Visual Analytics */}
          <Row className="g-3 mb-4">
            <Col lg={7}>
              <Card className="shadow-sm h-100 border-0" style={{ background: isDark ? "#202020" : "#ffffff", borderRadius: 12 }}>
                <Card.Header className="border-0 bg-transparent py-3 px-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
                  <h6 className="fw-bold mb-0" style={{ color: isDark ? "#ffffff" : "#0f172a" }}>
                    <i className="fas fa-chart-bar text-primary me-2" />
                    Financial Cash Flow &amp; Margin Summary
                  </h6>
                  <Badge bg="success-subtle" className="text-success border border-success-subtle fw-semibold">
                    Est. Gross Margin: {formatCurrency(Math.max(0, (summary.totalValue || 0) - (summary.totalCostAmount || 0)))}
                  </Badge>
                </Card.Header>
                <Card.Body className="p-3">
                  <ReactApexChart
                    options={{
                      chart: { type: "bar", height: 280, toolbar: { show: false }, background: "transparent" },
                      theme: { mode: isDark ? "dark" : "light" },
                      plotOptions: {
                        bar: {
                          distributed: true,
                          borderRadius: 6,
                          columnWidth: "45%",
                          dataLabels: { position: "top" },
                        },
                      },
                      colors: ["#0078D7", "#06B6D4", "#10B981", "#EF4444", "#8B5CF6"],
                      xaxis: {
                        categories: [
                          "WO Value",
                          "Billed",
                          "Collected",
                          "Total Outflow",
                          "Est. Margin",
                        ],
                        labels: {
                          style: {
                            fontSize: "11px",
                            fontWeight: 600,
                            fontFamily: "Poppins, sans-serif",
                            colors: isDark ? "#94a3b8" : "#64748b",
                          },
                        },
                        axisBorder: { color: isDark ? "rgba(255,255,255,0.1)" : "#e2e8f0" },
                        axisTicks: { color: isDark ? "rgba(255,255,255,0.1)" : "#e2e8f0" },
                      },
                      yaxis: {
                        labels: {
                          formatter: (val) => formatCompactCurrency(val),
                          style: {
                            colors: isDark ? "#94a3b8" : "#64748b",
                            fontSize: "11px",
                            fontFamily: "Poppins, sans-serif",
                          },
                        },
                      },
                      grid: {
                        borderColor: isDark ? "rgba(255, 255, 255, 0.08)" : "#f0f0f0",
                        strokeDashArray: 3,
                      },
                      dataLabels: {
                        enabled: true,
                        formatter: (val) => formatCompactCurrency(val),
                        offsetY: -20,
                        style: {
                          fontSize: "10.5px",
                          fontWeight: 600,
                          colors: [isDark ? "#ffffff" : "#0f172a"],
                        },
                      },
                      legend: { show: false },
                      tooltip: {
                        theme: isDark ? "dark" : "light",
                        y: { formatter: (val) => formatCurrency(val) },
                      },
                    }}
                    series={[
                      {
                        name: "Amount",
                        data: [
                          summary.totalValue || 0,
                          invoiceList.reduce((sum, inv) => sum + Number(inv.totalAmount || 0), 0),
                          invoiceList.reduce((sum, inv) => sum + Number(inv.paidAmount || 0), 0),
                          summary.totalCostAmount || summary.totalExpenseAmount || 0,
                          Math.max(0, (summary.totalValue || 0) - (summary.totalCostAmount || 0)),
                        ],
                      },
                    ]}
                    type="bar"
                    height={280}
                  />
                </Card.Body>
              </Card>
            </Col>
            <Col lg={5}>
              <Card className="shadow-sm h-100 border-0" style={{ background: isDark ? "#202020" : "#ffffff", borderRadius: 12 }}>
                <Card.Header className="border-0 bg-transparent py-3 px-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
                  <h6 className="fw-bold mb-0" style={{ color: isDark ? "#ffffff" : "#0f172a" }}>
                    <i className="fas fa-chart-pie text-warning me-2" />
                    Cost &amp; Outflow Distribution
                  </h6>
                  <Badge bg="danger-subtle" className="text-danger border border-danger-subtle fw-semibold">
                    {formatCompactCurrency(summary.totalCostAmount || 0)}
                  </Badge>
                </Card.Header>
                <Card.Body className="d-flex flex-column p-3">
                  <div className="my-auto">
                    {(summary.totalCostAmount || 0) > 0 ? (
                      <ReactApexChart
                        options={{
                          chart: { type: "donut", height: 260, background: "transparent" },
                          theme: { mode: isDark ? "dark" : "light" },
                          labels: ["Material POs", "Contractor Bills", "Vendor WO Bills", "Site Expenses"],
                          colors: ["#0078D7", "#F59E0B", "#8B5CF6", "#06B6D4"],
                          legend: {
                            position: "bottom",
                            fontSize: "12px",
                            labels: { colors: isDark ? "#cbd5e1" : "#334155" },
                          },
                          stroke: {
                            colors: [isDark ? "#202020" : "#ffffff"],
                            width: 2,
                          },
                          plotOptions: {
                            pie: {
                              donut: {
                                size: "68%",
                                labels: {
                                  show: true,
                                  name: {
                                    show: true,
                                    color: isDark ? "#94a3b8" : "#64748b",
                                    fontSize: "12px",
                                  },
                                  value: {
                                    show: true,
                                    color: isDark ? "#ffffff" : "#0f172a",
                                    fontSize: "15px",
                                    fontWeight: 700,
                                    formatter: (val) => formatCompactCurrency(val),
                                  },
                                  total: {
                                    show: true,
                                    label: "Total Outflow",
                                    color: isDark ? "#94a3b8" : "#64748b",
                                    formatter: () => formatCompactCurrency(summary.totalCostAmount || 0),
                                  },
                                },
                              },
                            },
                          },
                          tooltip: {
                            theme: isDark ? "dark" : "light",
                            y: { formatter: (val) => formatCurrency(val) },
                          },
                        }}
                        series={[
                          summary.totalPoAmount || 0,
                          summary.totalContractorBillingAmount || 0,
                          summary.totalVendorBillingAmount || 0,
                          summary.totalExpenseAmount || 0,
                        ]}
                        type="donut"
                        height={260}
                      />
                    ) : (
                      <div className="text-center py-4 text-muted">
                        <i className="fas fa-receipt fa-2x mb-2 d-block text-secondary opacity-50" />
                        No actual payment records logged for this project yet.
                      </div>
                    )}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <div className="d-flex align-items-center justify-content-between mb-3">
            <h6 className="fw-bold mb-0 d-flex align-items-center" style={{ color: isDark ? "#ffffff" : "#0f172a" }}>
              <i className="fas fa-file-invoice text-primary me-2" />
              Invoice &amp; Collection Summary
            </h6>
          </div>
          {renderInvoiceSummaryCards()}

          <Card className="mb-4 shadow-sm border-0" style={{ background: isDark ? "#202020" : "#ffffff", borderRadius: 12 }}>
            <Card.Header className="border-0 bg-transparent py-3 px-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
              <h6 className="fw-bold mb-0" style={{ color: isDark ? "#ffffff" : "#0f172a" }}>Generated Invoices</h6>
              <Badge bg="primary">{invoiceList.length} invoice(s)</Badge>
            </Card.Header>
            <Card.Body className="p-0">
              <Table responsive hover size="sm" className="mb-0 align-middle">
                <thead>
                  <tr style={{ background: isDark ? "rgba(255,255,255,0.03)" : "#f8fafc" }}>
                    <th className="py-2 px-3" style={{ color: isDark ? "#94a3b8" : "#64748b" }}>#</th>
                    <th className="py-2" style={{ color: isDark ? "#94a3b8" : "#64748b" }}>Invoice</th>
                    <th className="py-2" style={{ color: isDark ? "#94a3b8" : "#64748b" }}>Work Order</th>
                    <th className="py-2" style={{ color: isDark ? "#94a3b8" : "#64748b" }}>Status</th>
                    <th className="py-2 text-end" style={{ color: isDark ? "#94a3b8" : "#64748b" }}>Bill Generated</th>
                    <th className="py-2 text-end" style={{ color: isDark ? "#94a3b8" : "#64748b" }}>Paid</th>
                    <th className="py-2 text-end" style={{ color: isDark ? "#94a3b8" : "#64748b" }}>Deductions</th>
                    <th className="py-2 text-end" style={{ color: isDark ? "#94a3b8" : "#64748b" }}>Pending</th>
                    <th className="py-2 text-center" style={{ color: isDark ? "#94a3b8" : "#64748b" }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {invoiceList.length === 0 ? (
                    <tr>
                      <td colSpan="9" className="text-center text-muted py-4">
                        No invoices generated for this project
                      </td>
                    </tr>
                  ) : (
                    invoiceList.map((invoice, index) => {
                      const totalAmount = Number(invoice.totalAmount || 0);
                      const paidAmount = Number(invoice.paidAmount || 0);
                      const deductionAmount = Number(invoice.totalDeductionAmount || 0);
                      const pendingAmount = Math.max(
                        totalAmount - (paidAmount + deductionAmount),
                        0
                      );
                      return (
                        <tr key={invoice._id || index}>
                          <td className="px-3">{index + 1}</td>
                          <td className="fw-semibold">
                            {invoice.invoiceNumber || "-"}
                            {invoice.advisedInvoice?.fileUrl && (
                              <a
                                href={getFileUrl(invoice.advisedInvoice.fileUrl)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="badge bg-light text-primary border border-primary ms-1"
                                style={{ fontSize: "10px", textDecoration: "none" }}
                                title="View Advised Invoice"
                              >
                                <i className="fas fa-paperclip me-1"></i>Advice
                              </a>
                            )}
                          </td>
                          <td>
                            {invoice.workOrder?.woNo || invoice.workOrder?.woNumber || "-"}
                          </td>
                          <td>
                            <Badge bg={getStatusVariant(invoice.status)}>
                              {invoice.status || "-"}
                            </Badge>
                          </td>
                          <td className="text-end">{formatCurrency(totalAmount)}</td>
                          <td className="text-end text-success fw-semibold">
                            {formatCurrency(paidAmount)}
                          </td>
                          <td className="text-end">
                            {deductionAmount > 0 ? (
                              <span
                                className="badge bg-danger-subtle text-danger border border-danger-subtle fw-semibold"
                                style={{ fontSize: "11px" }}
                                title={
                                  invoice.deductions && invoice.deductions.length > 0
                                    ? invoice.deductions
                                      .map(
                                        (d) =>
                                          `${d.deductionTypeName || "Deduction"}: ${formatCurrency(
                                            d.amount
                                          )}`
                                      )
                                      .join(", ")
                                    : undefined
                                }
                              >
                                -{formatCurrency(deductionAmount)}
                              </span>
                            ) : (
                              <span className="text-muted">—</span>
                            )}
                          </td>
                          <td className="text-end text-danger fw-semibold">
                            {formatCurrency(pendingAmount)}
                          </td>
                          <td className="text-center">
                            <Button
                              size="sm"
                              variant="outline-primary"
                              onClick={() => handleViewInvoice(invoice._id)}
                            >
                              <i className="fas fa-eye me-1" />
                              View
                            </Button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>

          {/* Project-Wide Cost & Expense Outflows Tracker */}
          <Card className="mb-4 shadow-sm border-0" style={{ background: isDark ? "#202020" : "#ffffff", borderRadius: 12 }}>
            <Card.Header className="border-bottom py-3 px-3 bg-transparent">
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                <div>
                  <h6 className="fw-bold mb-0 d-flex align-items-center" style={{ color: isDark ? "#ffffff" : "#0f172a" }}>
                    <i className="fas fa-coins text-warning me-2" />
                    Project Expenses &amp; Cost Outflows (Actual Outflows)
                  </h6>
                  <small className="text-muted">
                    Consolidated view of all material PO payments, subcontractor bills, vendor WO bills, and site expenses for {selectedProjectName}
                  </small>
                </div>
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <Badge bg="primary-subtle" className="text-primary border border-primary-subtle px-3 py-2 fs-6">
                    {projectCostTrack.length} Total Payment Record(s)
                  </Badge>
                  <span className="badge bg-danger-subtle text-danger border border-danger-subtle fw-bold px-3 py-2 fs-6">
                    Total Outflow: {formatCurrency(summary.totalCostAmount || summary.totalExpenseAmount || 0)}
                  </span>
                </div>
              </div>
            </Card.Header>
            <Card.Body className="p-3">
              {/* Category Breakdown Metric Cards */}
              <Row className="g-2 mb-4">
                <Col xs={12} sm={6} lg={3}>
                  <div
                    className="p-3 border rounded d-flex justify-content-between align-items-center h-100 shadow-none"
                    style={{
                      background: isDark ? "rgba(255,255,255,0.03)" : "#f8fafc",
                      borderColor: isDark ? "rgba(255,255,255,0.08)" : "#e2e8f0",
                    }}
                  >
                    <div>
                      <div className="text-muted small fw-semibold">📦 Material POs</div>
                      <div className="fs-5 fw-bold mt-1" style={{ color: isDark ? "#ffffff" : "#0f172a" }}>
                        {formatCurrency(summary.totalPoAmount || 0)}
                      </div>
                    </div>
                    <Badge bg="primary-subtle" className="text-primary border border-primary-subtle px-2 py-1">
                      {projectCostTrack.filter((i) => i.categoryType === "PURCHASE_ORDER").length} PO(s)
                    </Badge>
                  </div>
                </Col>
                <Col xs={12} sm={6} lg={3}>
                  <div
                    className="p-3 border rounded d-flex justify-content-between align-items-center h-100 shadow-none"
                    style={{
                      background: isDark ? "rgba(255,255,255,0.03)" : "#f8fafc",
                      borderColor: isDark ? "rgba(255,255,255,0.08)" : "#e2e8f0",
                    }}
                  >
                    <div>
                      <div className="text-muted small fw-semibold">👷 Contractor Bills</div>
                      <div className="fs-5 fw-bold mt-1" style={{ color: isDark ? "#ffffff" : "#0f172a" }}>
                        {formatCurrency(summary.totalContractorBillingAmount || 0)}
                      </div>
                    </div>
                    <Badge bg="warning-subtle" className="text-warning-emphasis border border-warning-subtle px-2 py-1">
                      {projectCostTrack.filter((i) => i.categoryType === "CONTRACTOR_BILL").length} Bill(s)
                    </Badge>
                  </div>
                </Col>
                <Col xs={12} sm={6} lg={3}>
                  <div
                    className="p-3 border rounded d-flex justify-content-between align-items-center h-100 shadow-none"
                    style={{
                      background: isDark ? "rgba(255,255,255,0.03)" : "#f8fafc",
                      borderColor: isDark ? "rgba(255,255,255,0.08)" : "#e2e8f0",
                    }}
                  >
                    <div>
                      <div className="text-muted small fw-semibold">🏢 Vendor WO Bills</div>
                      <div className="fs-5 fw-bold mt-1" style={{ color: isDark ? "#ffffff" : "#0f172a" }}>
                        {formatCurrency(summary.totalVendorBillingAmount || 0)}
                      </div>
                    </div>
                    <Badge bg="secondary-subtle" className="text-secondary border border-secondary-subtle px-2 py-1">
                      {projectCostTrack.filter((i) => i.categoryType === "VENDOR_BILL").length} Bill(s)
                    </Badge>
                  </div>
                </Col>
                <Col xs={12} sm={6} lg={3}>
                  <div
                    className="p-3 border rounded d-flex justify-content-between align-items-center h-100 shadow-none"
                    style={{
                      background: isDark ? "rgba(255,255,255,0.03)" : "#f8fafc",
                      borderColor: isDark ? "rgba(255,255,255,0.08)" : "#e2e8f0",
                    }}
                  >
                    <div>
                      <div className="text-muted small fw-semibold">🧾 Direct / Site Expenses</div>
                      <div className="fs-5 fw-bold mt-1" style={{ color: isDark ? "#ffffff" : "#0f172a" }}>
                        {formatCurrency(summary.totalExpenseAmount || 0)}
                      </div>
                    </div>
                    <Badge bg="info-subtle" className="text-info-emphasis border border-info-subtle px-2 py-1">
                      {projectCostTrack.filter((i) => i.categoryType === "EXPENSE").length} Expense(s)
                    </Badge>
                  </div>
                </Col>
              </Row>

              {/* Filters & Search Control Bar */}
              <div
                className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3 p-3 rounded border"
                style={{
                  background: isDark ? "rgba(255,255,255,0.02)" : "#f8fafc",
                  borderColor: isDark ? "rgba(255,255,255,0.08)" : "#e2e8f0",
                }}
              >
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <span className="small text-muted fw-bold me-1">Category:</span>
                  <Button
                    size="sm"
                    variant={projectCostFilter === "ALL" ? "primary" : "outline-secondary"}
                    className="py-1 px-3"
                    onClick={() => setProjectCostFilter("ALL")}
                  >
                    All ({projectCostTrack.length})
                  </Button>
                  <Button
                    size="sm"
                    variant={projectCostFilter === "PURCHASE_ORDER" ? "primary" : "outline-secondary"}
                    className="py-1 px-3"
                    onClick={() => setProjectCostFilter("PURCHASE_ORDER")}
                  >
                    📦 Material POs ({projectCostTrack.filter((i) => i.categoryType === "PURCHASE_ORDER").length})
                  </Button>
                  <Button
                    size="sm"
                    variant={projectCostFilter === "CONTRACTOR_BILL" ? "primary" : "outline-secondary"}
                    className="py-1 px-3"
                    onClick={() => setProjectCostFilter("CONTRACTOR_BILL")}
                  >
                    👷 Contractor Bills ({projectCostTrack.filter((i) => i.categoryType === "CONTRACTOR_BILL").length})
                  </Button>
                  <Button
                    size="sm"
                    variant={projectCostFilter === "VENDOR_BILL" ? "primary" : "outline-secondary"}
                    className="py-1 px-3"
                    onClick={() => setProjectCostFilter("VENDOR_BILL")}
                  >
                    🏢 Vendor WO Bills ({projectCostTrack.filter((i) => i.categoryType === "VENDOR_BILL").length})
                  </Button>
                  <Button
                    size="sm"
                    variant={projectCostFilter === "EXPENSE" ? "primary" : "outline-secondary"}
                    className="py-1 px-3"
                    onClick={() => setProjectCostFilter("EXPENSE")}
                  >
                    🧾 Site Expenses ({projectCostTrack.filter((i) => i.categoryType === "EXPENSE").length})
                  </Button>
                </div>

                <div className="d-flex align-items-center gap-2 flex-wrap">
                  {workOrders.length > 1 && (
                    <div style={{ minWidth: "180px" }}>
                      <Form.Select
                        size="sm"
                        value={projectCostWoFilter}
                        onChange={(e) => setProjectCostWoFilter(e.target.value)}
                        style={{
                          background: isDark ? "#0f172a" : "#ffffff",
                          borderColor: isDark ? "rgba(255,255,255,0.12)" : "#ced4da",
                          color: isDark ? "#f1f5f9" : "#0f172a",
                        }}
                      >
                        <option value="ALL">All Work Orders</option>
                        {workOrders.map((wo) => (
                          <option key={wo._id} value={String(wo._id)}>
                            {wo.woNo || wo.woNumber || "Work Order"}
                          </option>
                        ))}
                      </Form.Select>
                    </div>
                  )}

                  <div style={{ minWidth: "220px" }}>
                    <div className="input-group input-group-sm">
                      <span
                        className="input-group-text"
                        style={{
                          background: isDark ? "#1e293b" : "#ffffff",
                          borderColor: isDark ? "rgba(255,255,255,0.12)" : "#ced4da",
                          color: isDark ? "#94a3b8" : "#64748b",
                        }}
                      >
                        <i className="fas fa-search" />
                      </span>
                      <Form.Control
                        type="text"
                        placeholder="Search vendor, doc no, remarks..."
                        value={projectCostSearch}
                        onChange={(e) => setProjectCostSearch(e.target.value)}
                        style={{
                          background: isDark ? "#0f172a" : "#ffffff",
                          borderColor: isDark ? "rgba(255,255,255,0.12)" : "#ced4da",
                          color: isDark ? "#f1f5f9" : "#0f172a",
                        }}
                      />
                      {projectCostSearch && (
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => setProjectCostSearch("")}
                        >
                          ✕
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Table */}
              <Table responsive bordered hover size="sm" className="mb-0 align-middle">
                <thead
                  className={isDark ? "" : "table-light"}
                  style={{
                    background: isDark ? "rgba(255,255,255,0.05)" : "#f8fafc",
                    color: isDark ? "#f1f5f9" : "#1e293b",
                  }}
                >
                  <tr>
                    <th style={{ width: "40px" }}>#</th>
                    <th>Date</th>
                    <th>Category</th>
                    <th>Work Order</th>
                    <th>Doc / Ref No</th>
                    <th>Paid To / Vendor</th>
                    <th>Status / Mode</th>
                    <th className="text-end">Paid Amount</th>
                    <th>Remarks / Receipt</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProjectCostTrack.length === 0 ? (
                    <tr>
                      <td colSpan="9" className="text-center text-muted py-4">
                        <i className="fas fa-folder-open fa-2x mb-2 d-block opacity-50" />
                        No actual payments or expenses found matching the selected filters.
                      </td>
                    </tr>
                  ) : (
                    filteredProjectCostTrack.map((item, itemIdx) => {
                      const isPO = item.categoryType === "PURCHASE_ORDER";
                      const isCB = item.categoryType === "CONTRACTOR_BILL";
                      const isVB = item.categoryType === "VENDOR_BILL";
                      const badgeBg = isPO ? "primary" : isCB ? "warning" : isVB ? "dark" : "info";
                      const badgeText = isCB ? "dark" : "white";

                      return (
                        <tr key={item._id || itemIdx}>
                          <td>{itemIdx + 1}</td>
                          <td>
                            {item.date
                              ? new Date(item.date).toLocaleDateString("en-IN", {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                              })
                              : "-"}
                          </td>
                          <td>
                            <Badge bg={badgeBg} text={badgeText} className="px-2 py-1">
                              {item.category}
                            </Badge>
                            {item.categorySub && (
                              <div className="small text-muted mt-1" style={{ fontSize: "11px" }}>
                                {item.categorySub}
                              </div>
                            )}
                          </td>
                          <td>
                            <Badge bg="light" text="dark" className="border fw-semibold">
                              {item.workOrderNo || "Project"}
                            </Badge>
                          </td>
                          <td>
                            <strong className="text-primary">{item.docNo || item.invoiceNumber || "—"}</strong>
                          </td>
                          <td className="fw-semibold">{item.paidTo || item.vendorName || "—"}</td>
                          <td>
                            <Badge bg="success" className="px-2 py-1">
                              {item.status || item.paymentMode || "Paid"}
                            </Badge>
                          </td>
                          <td className="text-end fw-bold text-success">
                            {formatCurrency(item.paidAmount || item.totalAmount)}
                          </td>
                          <td>
                            <div className="d-flex align-items-center gap-2 flex-wrap">
                              <span>{item.remarks || "—"}</span>
                              {item.bill?.filePath && (
                                <a
                                  href={getFileUrl(item.bill.filePath)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="badge bg-light text-primary border border-primary text-decoration-none"
                                  title="View Document"
                                >
                                  <i className="fas fa-paperclip me-1" /> View Receipt
                                </a>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
                {filteredProjectCostTrack.length > 0 && (
                  <tfoot
                    className={isDark ? "" : "table-light"}
                    style={{
                      background: isDark ? "rgba(255,255,255,0.05)" : "#f8fafc",
                      color: isDark ? "#f1f5f9" : "#1e293b",
                    }}
                  >
                    <tr>
                      <td colSpan="7" className="text-end fw-bold">
                        Filtered Total Outflow:
                      </td>
                      <td className="text-end fw-bold text-danger">
                        {formatCurrency(
                          filteredProjectCostTrack.reduce(
                            (sum, item) => sum + Number(item.paidAmount || item.totalAmount || 0),
                            0
                          )
                        )}
                      </td>
                      <td></td>
                    </tr>
                  </tfoot>
                )}
              </Table>
            </Card.Body>
          </Card>

          {workOrders.length === 0 ? (
            <Card className="shadow-sm">
              <Card.Body className="text-center py-5 text-muted">
                No work orders found for this project.
              </Card.Body>
            </Card>
          ) : (
            <div className="d-flex flex-column gap-4">
              {workOrders.map((wo) => {
                const woInvoices = invoiceList.filter((inv) => {
                  const invWoId =
                    typeof inv.workOrder === "object"
                      ? inv.workOrder?._id
                      : inv.workOrder;
                  return String(invWoId) === String(wo._id);
                });
                const woInvoiceDeductions = woInvoices.flatMap((inv) =>
                  (inv.deductions || []).map((d) => ({
                    ...d,
                    invoiceNumber: inv.invoiceNumber,
                    invoiceId: inv._id,
                  }))
                );
                const woInvoiceDeductionsTotal = woInvoiceDeductions.reduce(
                  (sum, d) => sum + Number(d.amount || 0),
                  0
                );

                return (
                  <Card className="shadow-sm" key={wo._id}>
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
                        <div>
                          <h5 className="fw-bold mb-1" style={{ color: isDark ? "#ffffff" : "#0f172a" }}>
                            {wo.woNo || wo.woNumber || "Work Order"}
                            {wo.label ? ` – ${wo.label}` : ""}
                          </h5>
                          <small className="text-muted">{wo.scope || "-"}</small>
                        </div>
                        <Badge bg={getStatusVariant(wo.status)}>{wo.status || "-"}</Badge>
                      </div>

                      <Row className="g-3 mb-3">
                        {[
                          { label: "Value", value: formatCurrency(wo.value) },
                          { label: "GST", value: formatCurrency(wo.taxAmount) },
                          { label: "Total", value: formatCurrency(wo.totalAmount) },
                          {
                            label: "Contract Deduction",
                            value: `${formatCurrency(wo.totalDeductionAmount)} (${formatPercent(
                              wo.totalDeductionPercentage
                            )})`,
                          },
                          {
                            label: "Invoice Deductions",
                            value: formatCurrency(woInvoiceDeductionsTotal),
                          },
                          { label: "Receivable", value: formatCurrency(wo.payableAmount) },
                          { label: "Receivable + GST", value: formatCurrency(wo.payableTotalAmount) },
                          {
                            label: "Invoices",
                            value: invoiceByWorkOrder[wo._id]?.count || 0,
                          },
                          {
                            label: "Bill Generated",
                            value: formatCurrency(invoiceByWorkOrder[wo._id]?.totalAmount),
                          },
                          {
                            label: "Paid",
                            value: formatCurrency(invoiceByWorkOrder[wo._id]?.paidAmount),
                          },
                          {
                            label: "Pending",
                            value: formatCurrency(invoiceByWorkOrder[wo._id]?.pendingAmount),
                          },
                          {
                            label: "Total Outflow",
                            value: formatCurrency(wo.totalCostAmount || wo.totalExpenseAmount),
                          },
                        ].map((item) => (
                          <Col xs={6} md={4} xl={2} key={item.label}>
                            <div
                              className="border rounded p-2 h-100"
                              style={{
                                background: isDark ? "rgba(255,255,255,0.03)" : "#f8fafc",
                                borderColor: isDark ? "rgba(255,255,255,0.08)" : "#e2e8f0",
                              }}
                            >
                              <small
                                className="text-muted d-block text-uppercase fw-semibold"
                                style={{ fontSize: "11px", letterSpacing: "0.5px" }}
                              >
                                {item.label}
                              </small>
                              <span className="fw-bold fs-6" style={{ color: isDark ? "#ffffff" : "#0f172a" }}>
                                {item.value}
                              </span>
                            </div>
                          </Col>
                        ))}
                      </Row>

                      <h6 className="fw-bold mt-4" style={{ color: isDark ? "#ffffff" : "#0f172a" }}>
                        Payment Terms
                      </h6>
                      <Table responsive bordered hover size="sm" className="mb-4">
                        <thead
                          className={isDark ? "" : "table-light"}
                          style={{
                            background: isDark ? "rgba(255,255,255,0.05)" : "#f8fafc",
                            color: isDark ? "#f1f5f9" : "#1e293b",
                          }}
                        >
                          <tr>
                            <th>#</th>
                            <th>Stage / Milestone</th>
                            <th className="text-end">%</th>
                            <th className="text-end">Without GST</th>
                            <th className="text-end">GST</th>
                            <th className="text-end">With GST</th>
                            <th className="text-end">Deduction</th>
                            <th className="text-end">Receivable</th>
                            <th className="text-end">Receivable + GST</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(wo.paymentTerms || []).length === 0 ? (
                            <tr>
                              <td colSpan="9" className="text-center text-muted">
                                No payment terms configured
                              </td>
                            </tr>
                          ) : (
                            wo.paymentTerms.map((term, index) => (
                              <tr key={term._id || index}>
                                <td>{index + 1}</td>
                                <td>{getTermLabel(term, wo.paymentMode)}</td>
                                <td className="text-end">{formatPercent(term.percentage)}</td>
                                <td className="text-end">{formatCurrency(term.amountWithoutGst)}</td>
                                <td className="text-end">{formatCurrency(term.gstAmount)}</td>
                                <td className="text-end">{formatCurrency(term.amountWithGst)}</td>
                                <td className="text-end">{formatCurrency(term.deductionAmount)}</td>
                                <td className="text-end">
                                  {formatCurrency(term.payableAmountWithoutGst)}
                                </td>
                                <td className="text-end">
                                  {formatCurrency(term.payableAmountWithGst)}
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </Table>

                      {/* Work Order Contract Deductions (% Terms) */}
                      <h6 className="fw-bold mt-4" style={{ color: isDark ? "#ffffff" : "#0f172a" }}>
                        Work Order Contract Deductions (% Terms)
                      </h6>
                      <Table responsive bordered hover size="sm" className="mb-4">
                        <thead
                          className={isDark ? "" : "table-light"}
                          style={{
                            background: isDark ? "rgba(255,255,255,0.05)" : "#f8fafc",
                            color: isDark ? "#f1f5f9" : "#1e293b",
                          }}
                        >
                          <tr>
                            <th>#</th>
                            <th>Type</th>
                            <th className="text-end">%</th>
                            <th className="text-end">Amount</th>
                            <th>Remarks</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(wo.deductions || []).length === 0 ? (
                            <tr>
                              <td colSpan="5" className="text-center text-muted">
                                No contract deductions configured
                              </td>
                            </tr>
                          ) : (
                            wo.deductions.map((deduction, index) => (
                              <tr key={deduction._id || index}>
                                <td>{index + 1}</td>
                                <td>{deduction.name?.name || deduction.name || "-"}</td>
                                <td className="text-end">{formatPercent(deduction.percentage)}</td>
                                <td className="text-end">{formatCurrency(deduction.amount)}</td>
                                <td>{deduction.remarks || "-"}</td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </Table>

                      {/* Invoice Settlement Deductions (Actual Deductions Applied) */}
                      <div className="d-flex justify-content-between align-items-center mt-4 mb-2">
                        <h6 className="fw-bold mb-0" style={{ color: isDark ? "#ffffff" : "#0f172a" }}>
                          Invoice Advice Deductions (Actual Deductions Applied)
                        </h6>
                        {woInvoiceDeductions.length > 0 && (
                          <span className="badge bg-light text-danger border border-danger fw-semibold">
                            Total Invoice Deductions: -{formatCurrency(woInvoiceDeductionsTotal)}
                          </span>
                        )}
                      </div>
                      <Table responsive bordered hover size="sm" className="mb-4 align-middle">
                        <thead
                          className={isDark ? "" : "table-light"}
                          style={{
                            background: isDark ? "rgba(255,255,255,0.05)" : "#f8fafc",
                            color: isDark ? "#f1f5f9" : "#1e293b",
                          }}
                        >
                          <tr>
                            <th>#</th>
                            <th>Invoice No</th>
                            <th>Deduction Type</th>
                            <th className="text-end">Amount</th>
                            <th>Remarks</th>
                          </tr>
                        </thead>
                        <tbody>
                          {woInvoiceDeductions.length === 0 ? (
                            <tr>
                              <td colSpan="5" className="text-center text-muted">
                                No invoice deductions added yet for this work order
                              </td>
                            </tr>
                          ) : (
                            woInvoiceDeductions.map((ded, idx) => (
                              <tr key={idx}>
                                <td>{idx + 1}</td>
                                <td>
                                  <strong className="text-primary">{ded.invoiceNumber}</strong>
                                </td>
                                <td>
                                  {ded.deductionTypeName || ded.deductionType?.name || "Deduction"}
                                </td>
                                <td className="text-end text-danger fw-semibold">
                                  -{formatCurrency(ded.amount)}
                                </td>
                                <td>{ded.remarks || "—"}</td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </Table>

                      {/* Work Order Cost & Expense Track (Actual Outflow) */}
                      {(() => {
                        const activeCostFilter = costFilters[wo._id] || "ALL";
                        const allCostItems = wo.costTrack || [];
                        const filteredCostItems = allCostItems.filter((item) => {
                          if (activeCostFilter === "ALL") return true;
                          return item.categoryType === activeCostFilter;
                        });

                        return (
                          <div className="mt-4 pt-3 border-top">
                            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                              <div className="d-flex align-items-center gap-2 flex-wrap">
                                <h6 className="fw-bold mb-0" style={{ color: isDark ? "#ffffff" : "#0f172a" }}>
                                  <i className="fas fa-coins text-warning me-2" />
                                  Work Order Cost &amp; Expense Track (Actual Outflow)
                                </h6>
                                <Badge bg="primary" className="px-2 py-1">
                                  {allCostItems.length} payment record(s)
                                </Badge>
                              </div>
                              <div className="d-flex align-items-center gap-2 flex-wrap">
                                <span className="badge bg-danger-subtle text-danger border border-danger-subtle fw-bold px-3 py-2">
                                  Total Outflow: {formatCurrency(wo.totalCostAmount || wo.totalExpenseAmount || 0)}
                                </span>
                              </div>
                            </div>

                            {/* Summary Metrics Cards */}
                            <div className="row g-2 mb-3">
                              <div className="col-md-3 col-sm-6">
                                <div
                                  className="p-3 border rounded d-flex justify-content-between align-items-center h-100 shadow-none"
                                  style={{
                                    background: isDark ? "rgba(255,255,255,0.03)" : "#f8fafc",
                                    borderColor: isDark ? "rgba(255,255,255,0.08)" : "#e2e8f0",
                                  }}
                                >
                                  <div>
                                    <div className="text-muted small fw-semibold">📦 Material POs</div>
                                    <div className="fs-6 fw-bold mt-1" style={{ color: isDark ? "#ffffff" : "#0f172a" }}>
                                      {formatCurrency(wo.totalPoAmount || 0)}
                                    </div>
                                  </div>
                                  <Badge bg="primary-subtle" className="text-primary border border-primary-subtle px-2 py-1">
                                    {(wo.purchaseOrders || []).length} PO(s)
                                  </Badge>
                                </div>
                              </div>
                              <div className="col-md-3 col-sm-6">
                                <div
                                  className="p-3 border rounded d-flex justify-content-between align-items-center h-100 shadow-none"
                                  style={{
                                    background: isDark ? "rgba(255,255,255,0.03)" : "#f8fafc",
                                    borderColor: isDark ? "rgba(255,255,255,0.08)" : "#e2e8f0",
                                  }}
                                >
                                  <div>
                                    <div className="text-muted small fw-semibold">👷 Contractor Bills</div>
                                    <div className="fs-6 fw-bold mt-1" style={{ color: isDark ? "#ffffff" : "#0f172a" }}>
                                      {formatCurrency(wo.totalContractorBillingAmount || 0)}
                                    </div>
                                  </div>
                                  <Badge bg="warning-subtle" className="text-warning-emphasis border border-warning-subtle px-2 py-1">
                                    {(wo.contractorBillings || []).length} Bill(s)
                                  </Badge>
                                </div>
                              </div>
                              <div className="col-md-3 col-sm-6">
                                <div
                                  className="p-3 border rounded d-flex justify-content-between align-items-center h-100 shadow-none"
                                  style={{
                                    background: isDark ? "rgba(255,255,255,0.03)" : "#f8fafc",
                                    borderColor: isDark ? "rgba(255,255,255,0.08)" : "#e2e8f0",
                                  }}
                                >
                                  <div>
                                    <div className="text-muted small fw-semibold">🏢 Vendor WO Bills</div>
                                    <div className="fs-6 fw-bold mt-1" style={{ color: isDark ? "#ffffff" : "#0f172a" }}>
                                      {formatCurrency(wo.totalVendorBillingAmount || 0)}
                                    </div>
                                  </div>
                                  <Badge bg="secondary-subtle" className="text-secondary border border-secondary-subtle px-2 py-1">
                                    {(wo.vendorBillings || []).length} Bill(s)
                                  </Badge>
                                </div>
                              </div>
                              <div className="col-md-3 col-sm-6">
                                <div
                                  className="p-3 border rounded d-flex justify-content-between align-items-center h-100 shadow-none"
                                  style={{
                                    background: isDark ? "rgba(255,255,255,0.03)" : "#f8fafc",
                                    borderColor: isDark ? "rgba(255,255,255,0.08)" : "#e2e8f0",
                                  }}
                                >
                                  <div>
                                    <div className="text-muted small fw-semibold">🧾 Direct / Site Expenses</div>
                                    <div className="fs-6 fw-bold mt-1" style={{ color: isDark ? "#ffffff" : "#0f172a" }}>
                                      {formatCurrency(wo.totalExpenseAmount || 0)}
                                    </div>
                                  </div>
                                  <Badge bg="info-subtle" className="text-info-emphasis border border-info-subtle px-2 py-1">
                                    {(wo.expenses || []).length} Expense(s)
                                  </Badge>
                                </div>
                              </div>
                            </div>

                            {/* Filter Tabs / Pills */}
                            <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
                              <span className="small text-muted fw-semibold me-1">Filter by:</span>
                              <Button
                                size="sm"
                                variant={activeCostFilter === "ALL" ? "primary" : "outline-secondary"}
                                className="py-1 px-3"
                                onClick={() => setCostFilters({ ...costFilters, [wo._id]: "ALL" })}
                              >
                                All ({allCostItems.length})
                              </Button>
                              <Button
                                size="sm"
                                variant={activeCostFilter === "PURCHASE_ORDER" ? "primary" : "outline-secondary"}
                                className="py-1 px-3"
                                onClick={() => setCostFilters({ ...costFilters, [wo._id]: "PURCHASE_ORDER" })}
                              >
                                📦 Material POs ({(wo.purchaseOrders || []).length})
                              </Button>
                              <Button
                                size="sm"
                                variant={activeCostFilter === "CONTRACTOR_BILL" ? "primary" : "outline-secondary"}
                                className="py-1 px-3"
                                onClick={() => setCostFilters({ ...costFilters, [wo._id]: "CONTRACTOR_BILL" })}
                              >
                                👷 Contractor Bills ({(wo.contractorBillings || []).length})
                              </Button>
                              <Button
                                size="sm"
                                variant={activeCostFilter === "VENDOR_BILL" ? "primary" : "outline-secondary"}
                                className="py-1 px-3"
                                onClick={() => setCostFilters({ ...costFilters, [wo._id]: "VENDOR_BILL" })}
                              >
                                🏢 Vendor WO Bills ({(wo.vendorBillings || []).length})
                              </Button>
                              <Button
                                size="sm"
                                variant={activeCostFilter === "EXPENSE" ? "primary" : "outline-secondary"}
                                className="py-1 px-3"
                                onClick={() => setCostFilters({ ...costFilters, [wo._id]: "EXPENSE" })}
                              >
                                🧾 Site Expenses ({(wo.expenses || []).length})
                              </Button>
                            </div>

                            {/* Table */}
                            <Table responsive bordered hover size="sm" className="mb-0 align-middle">
                              <thead
                                className={isDark ? "" : "table-light"}
                                style={{
                                  background: isDark ? "rgba(255,255,255,0.05)" : "#f8fafc",
                                  color: isDark ? "#f1f5f9" : "#1e293b",
                                }}
                              >
                                <tr>
                                  <th style={{ width: "40px" }}>#</th>
                                  <th>Date</th>
                                  <th>Category</th>
                                  <th>Doc / Ref No</th>
                                  <th>Paid To / Vendor</th>
                                  <th>Status / Mode</th>
                                  <th className="text-end">Paid Amount</th>
                                  <th>Remarks / Receipt</th>
                                </tr>
                              </thead>
                              <tbody>
                                {filteredCostItems.length === 0 ? (
                                  <tr>
                                    <td colSpan="8" className="text-center text-muted py-4">
                                      <i className="fas fa-folder-open me-2" />
                                      No actual payments recorded for this category
                                    </td>
                                  </tr>
                                ) : (
                                  filteredCostItems.map((item, itemIdx) => {
                                    const isPO = item.categoryType === "PURCHASE_ORDER";
                                    const isCB = item.categoryType === "CONTRACTOR_BILL";
                                    const isVB = item.categoryType === "VENDOR_BILL";
                                    const badgeBg = isPO ? "primary" : isCB ? "warning" : isVB ? "dark" : "info";
                                    const badgeText = isCB ? "dark" : "white";

                                    return (
                                      <tr key={item._id || itemIdx}>
                                        <td>{itemIdx + 1}</td>
                                        <td>
                                          {item.date
                                            ? new Date(item.date).toLocaleDateString("en-IN", {
                                              day: "2-digit",
                                              month: "short",
                                              year: "numeric",
                                            })
                                            : "-"}
                                        </td>
                                        <td>
                                          <Badge bg={badgeBg} text={badgeText} className="px-2 py-1">
                                            {item.category}
                                          </Badge>
                                          {item.categorySub && (
                                            <div className="small text-muted mt-1" style={{ fontSize: "11px" }}>
                                              {item.categorySub}
                                            </div>
                                          )}
                                        </td>
                                        <td>
                                          <strong className="text-primary">{item.docNo || item.invoiceNumber || "—"}</strong>
                                        </td>
                                        <td className="fw-semibold">{item.paidTo || item.vendorName || "—"}</td>
                                        <td>
                                          <Badge bg="success" className="px-2 py-1">
                                            {item.status || item.paymentMode || "Paid"}
                                          </Badge>
                                        </td>
                                        <td className="text-end fw-bold text-success">
                                          {formatCurrency(item.paidAmount || item.totalAmount)}
                                        </td>
                                        <td>
                                          <div className="d-flex align-items-center gap-2 flex-wrap">
                                            <span>{item.remarks || "—"}</span>
                                            {item.bill?.filePath && (
                                              <a
                                                href={getFileUrl(item.bill.filePath)}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="badge bg-light text-primary border border-primary text-decoration-none"
                                                title="View Document"
                                              >
                                                <i className="fas fa-paperclip me-1" /> View Receipt
                                              </a>
                                            )}
                                          </div>
                                        </td>
                                      </tr>
                                    );
                                  })
                                )}
                              </tbody>
                            </Table>
                          </div>
                        );
                      })()}
                    </Card.Body>
                  </Card>
                );
              })}
            </div>
          )}
        </>
      )}

      <Modal
        show={!!selectedInvoice || invoiceLoading}
        onHide={() => setSelectedInvoice(null)}
        size="lg"
        centered
        contentClassName={isDark ? "bg-dark text-white border border-secondary" : ""}
      >
        <Modal.Header closeButton closeVariant={isDark ? "white" : undefined} className={isDark ? "border-secondary" : ""}>
          <Modal.Title style={{ color: isDark ? "#ffffff" : "#0f172a" }}>Invoice Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {invoiceLoading ? (
            <div className="text-center py-4">
              <Spinner animation="border" />
            </div>
          ) : selectedInvoice ? (
            <>
              <Row className="g-3 mb-4">
                <Col md={6}>
                  <strong style={{ color: isDark ? "#94a3b8" : "#64748b" }}>Invoice: </strong>
                  <span className="fw-semibold" style={{ color: isDark ? "#ffffff" : "#0f172a" }}>{selectedInvoice.invoiceNumber || "-"}</span>
                  {selectedInvoice.advisedInvoice?.fileUrl && (
                    <a
                      href={getFileUrl(selectedInvoice.advisedInvoice.fileUrl)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="badge bg-info text-white text-decoration-none ms-2"
                    >
                      <i className="fas fa-external-link-alt me-1"></i>View Advised Document
                    </a>
                  )}
                </Col>
                <Col md={6}>
                  <strong style={{ color: isDark ? "#94a3b8" : "#64748b" }}>Status: </strong>{" "}
                  <Badge bg={getStatusVariant(selectedInvoice.status)}>
                    {selectedInvoice.status || "-"}
                  </Badge>
                </Col>
                <Col md={6}>
                  <strong style={{ color: isDark ? "#94a3b8" : "#64748b" }}>Project: </strong>
                  <span className="fw-semibold" style={{ color: isDark ? "#ffffff" : "#0f172a" }}>{selectedInvoice.project?.projectName || "-"}</span>
                </Col>
                <Col md={6}>
                  <strong style={{ color: isDark ? "#94a3b8" : "#64748b" }}>Work Order: </strong>{" "}
                  <span className="fw-semibold" style={{ color: isDark ? "#ffffff" : "#0f172a" }}>{selectedInvoice.workOrder?.woNo || selectedInvoice.workOrder?.woNumber || "-"}</span>
                </Col>
                <Col md={3}>
                  <div
                    className="border rounded p-2"
                    style={{
                      background: isDark ? "rgba(255,255,255,0.03)" : "#f8fafc",
                      borderColor: isDark ? "rgba(255,255,255,0.08)" : "#e2e8f0",
                    }}
                  >
                    <small className="text-muted d-block text-uppercase fw-semibold" style={{ fontSize: "11px" }}>Total</small>
                    <strong className="fs-6" style={{ color: isDark ? "#ffffff" : "#0f172a" }}>{formatCurrency(selectedInvoice.totalAmount)}</strong>
                  </div>
                </Col>
                <Col md={3}>
                  <div
                    className="border rounded p-2"
                    style={{
                      background: isDark ? "rgba(255,255,255,0.03)" : "#f8fafc",
                      borderColor: isDark ? "rgba(255,255,255,0.08)" : "#e2e8f0",
                    }}
                  >
                    <small className="text-muted d-block text-uppercase fw-semibold" style={{ fontSize: "11px" }}>Paid</small>
                    <strong className="fs-6 text-success">
                      {formatCurrency(selectedInvoice.paidAmount)}
                    </strong>
                  </div>
                </Col>
                <Col md={3}>
                  <div
                    className="border rounded p-2"
                    style={{
                      background: isDark ? "rgba(255,255,255,0.03)" : "#f8fafc",
                      borderColor: isDark ? "rgba(255,255,255,0.08)" : "#e2e8f0",
                    }}
                  >
                    <small className="text-muted d-block text-uppercase fw-semibold" style={{ fontSize: "11px" }}>Deductions</small>
                    <strong className="fs-6 text-danger">
                      {Number(selectedInvoice.totalDeductionAmount) > 0
                        ? `-${formatCurrency(selectedInvoice.totalDeductionAmount)}`
                        : "₹ 0.00"}
                    </strong>
                  </div>
                </Col>
                <Col md={3}>
                  <div
                    className="border rounded p-2"
                    style={{
                      background: isDark ? "rgba(255,255,255,0.03)" : "#f8fafc",
                      borderColor: isDark ? "rgba(255,255,255,0.08)" : "#e2e8f0",
                    }}
                  >
                    <small className="text-muted d-block text-uppercase fw-semibold" style={{ fontSize: "11px" }}>Pending</small>
                    <strong className="fs-6 text-danger">
                      {formatCurrency(
                        Math.max(
                          Number(selectedInvoice.totalAmount || 0) -
                          (Number(selectedInvoice.paidAmount || 0) +
                            Number(selectedInvoice.totalDeductionAmount || 0)),
                          0
                        )
                      )}
                    </strong>
                  </div>
                </Col>
              </Row>

              <h6 className="fw-bold mb-3" style={{ color: isDark ? "#ffffff" : "#0f172a" }}>Item Breakdown</h6>
              <Table responsive bordered hover size="sm" className="mb-0 align-middle">
                <thead
                  className={isDark ? "" : "table-light"}
                  style={{
                    background: isDark ? "rgba(255,255,255,0.05)" : "#f8fafc",
                    color: isDark ? "#f1f5f9" : "#1e293b",
                  }}
                >
                  <tr>
                    <th>#</th>
                    <th>Item</th>
                    <th className="text-end">Qty</th>
                    <th className="text-end">Price</th>
                    <th className="text-end">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {(selectedInvoice.items || []).map((item, index) => (
                    <tr key={index}>
                      <td>{index + 1}</td>
                      <td>{item.itemName || item.name || "-"}</td>
                      <td className="text-end">{item.quantity}</td>
                      <td className="text-end">{formatCurrency(item.price)}</td>
                      <td className="text-end fw-semibold">
                        {formatCurrency(Number(item.quantity || 0) * Number(item.price || 0))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </>
          ) : null}
        </Modal.Body>
      </Modal>
    </>
  );
};

export default Payments;
